import React, { useEffect, useRef, useState, useCallback } from 'react';
import { installIosSafariBars } from './utils/iosSafariBars';
import { StreamPixelApplication, Flags, EditTextModal, RELAY_UNAVAILABLE_CODE, RELAY_UNAVAILABLE_REASON, relayUnavailableMessage } from 'streampixel';
import axios from 'axios';
import { isTablet, isDesktop, isIOS, osName, browserName } from 'react-device-detect';
import { uuidv7 } from "uuidv7";

import LoadingUI from './LoadingUi';
import './loading.css';
import VoiceChatUI from './components/VoiceChatUI';

import { resolutionDef, QP_BY_CODEC, HOST_WAIT_MS, SFU_LOADING_TEXT } from './constants/streamConstants';
import {
  CLOSE_KIND, STREAMER_WAIT_BACKSTOP_MS, streamerLostAutoRetryDelay, classifyClose, shouldAutoRetry,
  terminateAction, keepsPinOnRetry, isRetryableTicketFailure, composeStreamerLostMessage,
  closedByViewerMessage, retryFailedMessage, gaveUpMessage, gaveUpDetail, VIEWER_CLOSED_REASON,
  isConnectionUp, classifyConnectTimeout, armsConnectVerdict, connectTimeoutVerdict,
  admissionRefusedMessage, ADMISSION_REQUIRED_CODE,
} from './utils/streamerLoss';
import { isMobilePhone, getDeviceTypeByUserAgent } from './utils/deviceUtils';
import { isTouchDeviceMessage } from './utils/touchDevice';
import { takeHandover, pinRefusalMessage } from './utils/admissionHandover';
import { generateRandomName, createUserAvatar } from './utils/avatarUtils';
import { showLoadingSpinner, hideLoadingSpinner } from './utils/spinnerUtils';
import { captureScreenshot } from './utils/screenshotUtils';
import { getParentFrameUrl, getAttributionUrl } from './utils/urlUtils';
import {
  getWebRtcDiagnostics,
  logWebRtcDiagnostics,
  isTcpRelayActive,
  getSelectedPathInfo,
} from './utils/webRtcDiagnostics';

import StatsModal from './components/StatsModal';
import TcpFallbackToast from './components/TcpFallbackToast';
import ControlPanel from './components/ControlPanel';
import LoadingOverlay, { INITIAL_AUTH_MESSAGE } from './components/LoadingOverlay';
import { BrandLogo } from './components/StatusCard';
import DisconnectScreen from './components/DisconnectScreen';

// ----- Incoming file (UE "Send File") UI -----
// A single glass "file transfer" card, bottom-right: shows the file icon (or an
// image thumbnail), name, size and a live progress bar while receiving, then a
// completed state with the right delivery action for the platform.

function spFmtBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function spEnsureFileCardStyles() {
  if (document.getElementById('sp-file-card-styles')) return;
  const s = document.createElement('style');
  s.id = 'sp-file-card-styles';
  s.textContent = `
    @keyframes spCardIn { from { opacity:0; transform:translateY(-16px) scale(.98); } to { opacity:1; transform:none; } }
    #sp-file-card { position:fixed; right:24px; top:24px; z-index:2147483647; width:340px; max-width:calc(100vw - 32px);
      background:rgba(28,28,32,.72); backdrop-filter:blur(24px) saturate(1.5); -webkit-backdrop-filter:blur(24px) saturate(1.5);
      border:1px solid rgba(255,255,255,.12); border-radius:18px; box-shadow:0 8px 32px rgba(0,0,0,.44), 0 1px 0 rgba(255,255,255,.06) inset;
      color:#f4f4f5; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; padding:14px 16px; letter-spacing:-.01em;
      animation:spCardIn .28s cubic-bezier(.2,.8,.2,1); box-sizing:border-box; }
    #sp-file-card .sp-row { display:flex; align-items:center; gap:12px; }
    #sp-file-card .sp-icon { width:44px; height:44px; flex:0 0 44px; border-radius:10px; background:rgba(255,255,255,.08);
      display:flex; align-items:center; justify-content:center; font-size:20px; overflow:hidden;
      background-size:cover; background-position:center; }
    #sp-file-card .sp-info { flex:1 1 auto; min-width:0; }
    #sp-file-card .sp-name { font-size:14px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    #sp-file-card .sp-sub { font-size:12px; color:#a1a1aa; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    #sp-file-card .sp-close { flex:0 0 auto; background:none; border:none; color:#a1a1aa; font-size:20px; line-height:1;
      cursor:pointer; padding:2px 6px; border-radius:6px; }
    #sp-file-card .sp-close:hover { color:#fff; background:rgba(255,255,255,.08); }
    #sp-file-card .sp-track { height:6px; background:rgba(255,255,255,.12); border-radius:99px; overflow:hidden; margin-top:12px; }
    #sp-file-card .sp-bar { height:100%; width:0%; border-radius:99px; background:#0A84FF; transition:width .2s cubic-bezier(.2,.8,.2,1); }
    #sp-file-card .sp-btn { display:block; width:100%; margin-top:12px; padding:10px 14px; border:none; border-radius:10px;
      background:#0A84FF; color:#fff; font-size:14px; font-weight:600; letter-spacing:-.01em; cursor:pointer; transition:filter .15s ease; }
    #sp-file-card .sp-btn:hover { filter:brightness(1.08); }
    #sp-file-card .sp-btn:active { filter:brightness(.94); }
  `;
  document.head.appendChild(s);
}

function spGetFileCard(create) {
  let el = document.getElementById('sp-file-card');
  if (!el && create) {
    spEnsureFileCardStyles();
    el = document.createElement('div');
    el.id = 'sp-file-card';
    el.innerHTML = `
      <div class="sp-row">
        <div class="sp-icon" data-role="icon">📄</div>
        <div class="sp-info">
          <div class="sp-name" data-role="name"></div>
          <div class="sp-sub" data-role="sub"></div>
        </div>
        <button class="sp-close" data-role="close" aria-label="Dismiss">×</button>
      </div>
      <div class="sp-track" data-role="track"><div class="sp-bar" data-role="bar"></div></div>`;
    document.body.appendChild(el);
    el.querySelector('[data-role=close]').onclick = () => spRemoveFileCard();
  }
  return el;
}

function spRemoveFileCard() {
  const el = document.getElementById('sp-file-card');
  if (!el) return;
  if (el.dataset.thumbUrl) { try { URL.revokeObjectURL(el.dataset.thumbUrl); } catch (e) {} }
  el.remove();
}

// Epic's file-transfer protocol sends only the extension, not a name, so we build
// one. Strip any leading dot on the extension — that is what produced "transfer..png".
function spFileName(f) {
  if (!f) return 'capture';
  // Memoise only the TIMESTAMP per transfer (stable across progress + delivery,
  // reset in the FileExtension handler); recompute the extension every call so the
  // final download name still picks it up even if it landed after the first tick.
  if (!f.spStamp) {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    f.spStamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  }
  const ext = String(f.extension || '').replace(/^\.+/, '').trim().toLowerCase();
  return ext ? `capture-${f.spStamp}.${ext}` : `capture-${f.spStamp}`;
}

// Live progress while a transfer is in flight.
function updateFileProgress(received, total, filename, receivedBytes) {
  if (!total) { spRemoveFileCard(); return; }
  const el = spGetFileCard(true);
  if (el.dataset.complete) return; // don't overwrite the completed state
  const pct = Math.min(100, Math.round((received / total) * 100));
  if (filename) el.querySelector('[data-role=name]').textContent = filename;
  el.querySelector('[data-role=sub]').textContent = receivedBytes
    ? `Receiving… ${pct}% · ${spFmtBytes(receivedBytes)}`
    : `Receiving… ${pct}%`;
  el.querySelector('[data-role=bar]').style.width = `${pct}%`;
}

// Transfer complete: switch the card to its finished state and deliver the file.
//  - Desktop: auto-download, then offer a "Download again" button; auto-dismisses.
//  - iOS Safari: it ignores the download attribute and blocks non-gesture
//    downloads, so we show a "Save" button; on tap (a real gesture) we open the
//    native share sheet (Save Image / Save to Files).
function deliverReceivedFile(blob, filename, mimetype) {
  const type = mimetype || 'application/octet-stream';
  const isImage = type.startsWith('image/');
  const el = spGetFileCard(true);
  el.dataset.complete = '1';
  el.querySelector('[data-role=name]').textContent = filename;
  el.querySelector('[data-role=sub]').textContent = `${spFmtBytes(blob.size)} · ready`;

  const icon = el.querySelector('[data-role=icon]');
  if (isImage) {
    const thumbUrl = URL.createObjectURL(blob);
    el.dataset.thumbUrl = thumbUrl;
    icon.style.backgroundImage = `url("${thumbUrl}")`;
    icon.textContent = '';
  } else {
    icon.textContent = '📄';
  }

  const track = el.querySelector('[data-role=track]');
  if (track) track.remove();
  let btn = el.querySelector('.sp-btn');
  if (!btn) { btn = document.createElement('button'); btn.className = 'sp-btn'; el.appendChild(btn); }

  if (isIOS) {
    btn.textContent = `Save ${isImage ? 'image' : 'file'}`;
    btn.onclick = async () => {
      try {
        const file = new File([blob], filename, { type });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: filename });
        } else {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 30000);
        }
      } catch (e) {
        // user cancelled — keep the button so they can retry
      }
    };
  } else {
    const doDownload = () => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    };
    doDownload();
    el.querySelector('[data-role=sub]').textContent = `${spFmtBytes(blob.size)} · downloaded`;
    btn.textContent = 'Download again';
    btn.onclick = doDownload;
    setTimeout(() => spRemoveFileCard(), 10000); // auto-dismiss on desktop
  }
}

// Module-level mutable state — intentional for cross-render access inside stream callbacks
let isMobile = isMobilePhone();
let geoData = {};
// Launch parameters from the share URL (?map=showroom&car=corolla — every key
// but the auth/pin ones), sent to Unreal once per SESSION. Built once at mount
// and never cleared: a fresh session (Retry, "Start again", the automatic
// retry after a loss) is a NEW Unreal instance that needs them again — on main
// each of those was a page reload, which rebuilt them from the URL. A
// reconnect inside one attempt is the same instance and does not get them
// twice (startPlay's launchParamsSent).
const launchParams = {};
// SaaS (EXPLICIT_CLOSE off): the launch parameters go out ONCE per page, then never again — main's behaviour, kept
// byte-for-byte (a SaaS page has no in-place fresh session; each new session is a reload).
let ueMessage = {};
let reconnectWindowStart = null;
let reconnecting = false;
// The 5 s wait before a reconnect (the close handler's reconnect branch). A
// Close during it cancels it (handleTerminateSession).
let reconnectTimer = null;
let sfuRecoveryLoop = null;
let psNegotiationListener = null;
let lastNegotiationRecoverAt = 0;
let hostWaitTimer = null;
// Streamer loss (close 4006 "Streamer Disconnected" — the host or the app went
// away mid-session, see utils/streamerLoss.js). ONE automatic fresh-session
// attempt per page load — deliberately never reset: a second loss in the same
// page life is the viewer's call, not a loop — after that the viewer presses
// Retry.
let streamerLostAutoRetried = false;
let streamerLostRetryTimer = null;
// The viewer asked for the close (the embedder's terminateSession control
// message — the SDK's Close — or the page-leave path): the close event that
// follows is 'closed', never 'lost', whatever code the server echoes. Set
// before the socket is closed, cleared when a stream is built or retried. This
// flag, not the echoed 1000 "viewer-closed", is what makes a close 'closed'.
let viewerClosed = false;
// The close came from the page-leave path (pagehide) and the page is still
// here (restored from the bfcache): the card says the stream was closed when
// the viewer left, not that they pressed Close. Set next to viewerClosed,
// reset with it.
let closedOnLeave = false;
// The viewer asked for the close (terminateSession) while a fresh session was
// being started and no player existed yet — the ticket mint or the 1 s connect
// delay of a first load, a Retry, a "Start again" or the auto-retry. The
// "Stream closed" card is already up; this stops that start from building a
// player behind it (the connect effect checks it before startPlay) and keeps a
// transient mint failure from replacing the card (showRetryFailed). Cleared by
// the next Retry / "Start again".
let closeRequested = false;
// The attempt that is up (or that a card stands for) ended in a streamer loss:
// its host is dead. Read when the next fresh session decides whether a pinned
// test session keeps its pin (keepsPinOnRetry hostLost), even when a Close put
// "Stream closed" over the loss card. Reset when a new stream is built.
let streamerLostThisAttempt = false;
// The window 'offline' handler of the stream that is up (onVideoInitialized),
// kept by name so teardownStream can remove it — one per stream, not one per
// reconnect and per retry for the page's life.
let offlineListener = null;
// The player gave up waiting for the streamer on its own (utils/streamerLoss
// 'gave-up') and is closing the socket it was waiting on: that close is the
// player's doing, not a new event — the card is already up.
let gaveUpWaiting = false;
// Backstop for the wait between "connected to signalling" and the first SDP
// offer (STREAMER_WAIT_BACKSTOP_MS, 5 min: signalling's own 180 s wait plus
// placement time);
// CLEARED by a queue-position message (the server owns a queued viewer's
// wait) and by the offer.
let streamerWaitTimer = null;
// The two post-SDP-answer classifier timers (+30 s log, +60 s verdict) of the
// attempt that is up. Cleared by teardownStream (every disconnect, every
// retry) so a timer armed by a previous attempt can never pass judgement on —
// and end — the next one; the callbacks also check they are still armed.
let streamerClassifierTimers = [];
// Which card the viewer's Retry / "Start again" came from, so a mint that
// fails transiently puts the SAME card back (a viewer who pressed Close must
// not then read "connection lost").
let retryFromKind = null;
// A fresh session after a loss, a give-up or a Close is being minted
// (retryFreshSession → authenticate). While set, a transient failure of the
// access lookup or the ticket mint puts back the card the viewer came from
// (retryFromKind) with its button, instead of landing on the "unavailable"
// card, which offers nothing but a reload.
let retryingAfterLoss = false;
// StreamPixelApplication's own listener cleanup for the stream that is up.
let disposeStream = null;
// The streamer id came from the URL (an SFU room link, or an explicit
// streamerId=): a retry must keep it. A generated id is replaced on retry, so
// the fresh session launches its own app rather than naming the dead one.
let streamerIdFromUrl = false;

// The password a viewer typed, kept for THIS TAB only (sessionStorage: gone when the tab closes, never
// shared with other tabs or sites). A page reload — the SFU recovery below does one, so does a viewer
// pressing F5 — re-runs the access check, and without this every reload re-asked for the password
// (Swarnim Evara, 2026-10-09: a guest saw the password dialog every few seconds). A remembered password
// is tried silently; only a rejected one brings the dialog back.
const PW_KEY = (projectId) => `sp:pw:${projectId}`;
const rememberPassword = (projectId, pw) => { try { sessionStorage.setItem(PW_KEY(projectId), pw); } catch (_) { /* storage blocked in a third-party frame */ } };
const rememberedPassword = (projectId) => { try { return sessionStorage.getItem(PW_KEY(projectId)); } catch (_) { return null; } };
const forgetPassword = (projectId) => { try { sessionStorage.removeItem(PW_KEY(projectId)); } catch (_) { /* ignore */ } };
let PixelStreamingApp;
let PixelStreamingUiApp;
let activeResolution;
let resolutionSetting = [];
let message;

let userName = generateRandomName();

let avatar = createUserAvatar(userName);
// The env var is the BARE origin (e.g. https://platform.staging.streampixel.io).
// platform-api serves everything under /api/v1, so the code owns that prefix —
// appended once here, with any trailing slash stripped first, so the Vercel value
// stays a clean origin (no //, no missing-/api/v1 footgun). This base is also
// handed to the voice-chat SDK as apiBase, which expects the /api/v1 base too.
// A DEDICATED (on-prem) deployment configures the player at RUN time instead:
// /spx-config.js, written by the container from its environment, sets
// window.__SPX_CONFIG__ = { apiOrigin, telemetryOrigin, playerDomains }. One
// image then serves every customer and environment; the build-time variables
// below stay the SaaS path and are untouched when the runtime config is empty.
const SPX_CONFIG = (typeof window !== 'undefined' && window.__SPX_CONFIG__) || {};
// The dedicated (on-prem) image is built with REACT_APP_EDITION=onprem
// (docker/Dockerfile); the SaaS build leaves it unset.
const IS_ONPREM = process.env.REACT_APP_EDITION === 'onprem';
// The explicit-close viewer lifecycle (audit A3, 2026-10-25) — dedicated edition only, decided at BUILD time like
// IS_ONPREM, so the SaaS bundle keeps today's behaviour exactly (the branches below compile out of it):
//   * Close and page leave end the session on purpose: 1000 "viewer-closed" + a POST /session/end beacon, so the seat
//     is freed at once instead of signalling's ~70 s drop hold; the "Stream closed" card with "Start again";
//   * streamer loss (4006 "Streamer Disconnected": host lost, app crashed, forced drain) shows "Connection lost" and
//     makes ONE automatic fresh-session attempt (jittered 2–6 s, visible tab only), then a Retry button;
//   * a stream that never comes (the 60 s mute-streamer verdict, a 5 min pre-offer backstop, 4005) ends as a give-up
//     with a Retry, never an automatic one; a refused admission (4401) has its own terminal card.
// It needs signalling that sends 4006 "Streamer Disconnected" on every loss path and serves /session/end — the runtime's
// signalling has both (closeCodes.ts, sessionEndRoute.ts). utils/streamerLoss.js holds every decision.
const EXPLICIT_CLOSE = IS_ONPREM;
// Where the signalling admission ticket travels (P-39). Default, and the SaaS:
// in the WebSocket URL, as always. `ticketInUrl: false` in the runtime config
// (SPX_TICKET_IN_URL=false): as the first signalling message instead, so it
// never reaches a URL or an access log — needs signalling that reads it.
const TICKET_IN_URL = SPX_CONFIG.ticketInUrl !== false;
// A ticket the dedicated dashboard minted for an operator's pinned test session
// and handed over in the URL fragment (#admission=…; the whole flow is described
// in utils/admissionHandover.js). Read once, when the bundle loads, and taken out
// of the address bar there and then. The dedicated edition only: the SaaS never
// looks at the fragment.
let handedOver = null;
if (IS_ONPREM && typeof window !== 'undefined') {
  handedOver = takeHandover(window.location, window.history);
}
// The handed ticket was used for this page's session: a Retry does not mint an
// ordinary session in its place (one test, one session).
let handoverUsed = false;
// The current session's ticket came from the dashboard: it goes as the first
// signalling message, never in the URL, whatever TICKET_IN_URL says.
let usingHandedTicket = false;
// The text/voice/video chat server. The SaaS keeps the SDK's built-in LiveKit
// Cloud project. A dedicated deployment names its own (SPX_LIVEKIT_URL) or has
// none: '' turns chat's connection off rather than reaching a third party (P-34).
const LIVEKIT_URL = SPX_CONFIG.livekitUrl || (IS_ONPREM ? '' : undefined);
const PLATFORM_API_ORIGIN = SPX_CONFIG.apiOrigin || process.env.REACT_APP_PLATFORM_API_URL;
const API_URL = PLATFORM_API_ORIGIN
  ? `${PLATFORM_API_ORIGIN.replace(/\/+$/, '')}/api/v1`
  : undefined;

// Local dev mode no longer needs a frontend signalling-host env var: the dev
// ticket response carries config.signallingHost (the backend is the single
// source), read off `project` in startPlay exactly like a normal stream's host.

// The hosts the player is served from. Anything else means the page is being
// served on a CUSTOMER's domain, which identifies their project.
//
// main hardcoded `host.endsWith('streampixel.io')`. That is one deployment's
// domain: on-prem serves the player from the customer's own, so every page load
// there looks like a custom domain and triggers a lookup that cannot succeed.
// Configurable, with the SaaS value as the default so nothing changes here.
const PLAYER_DOMAINS = (SPX_CONFIG.playerDomains || process.env.REACT_APP_PLAYER_DOMAINS || 'streampixel.io')
  .split(',').map((d) => d.trim().toLowerCase()).filter(Boolean);
// Bare origin (e.g. https://telemetry.staging.streampixel.io); append the ingest
// path here. Logs + WebRTC diagnostics are POSTed to this exact URL.
const TELEMETRY_ORIGIN = SPX_CONFIG.telemetryOrigin || process.env.REACT_APP_TELEMETRY_URL;
const LOGGER_URL = TELEMETRY_ORIGIN
  ? `${TELEMETRY_ORIGIN.replace(/\/+$/, '')}/api/frontend-streaming-logs`
  : undefined;

const isCustomDomain = (host) =>
  Boolean(host) && !PLAYER_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));

/**
 * STARTED AT MODULE LOAD, not inside the effect that needs it.
 *
 * On a custom domain the project id is unknown until this returns, so it sits
 * directly in front of everything: no stream can start without it. Waiting for
 * React to mount before ASKING adds the whole mount to that delay for no reason —
 * the host is known the instant this file is parsed, and the network is idle then.
 *
 * Kicking it off here overlaps the request with React mounting, so by the time the
 * effect wants an id the answer has usually arrived. It is a promise, not a value:
 * the effect awaits it and gets whatever state it is in.
 *
 * Deliberately NOT an edge rewrite. That version was faster still and tied the
 * feature to one host — it cannot work on-prem, and it bills per request. This
 * costs nothing, runs anywhere the player runs, and gives up only the milliseconds
 * between page load and script parse.
 *
 * Failures are swallowed to null. The caller decides what to do about it, and an
 * unhandled rejection at module scope would surface as a console error on every
 * custom-domain load.
 */
/**
 * Retry the two calls a viewer CANNOT start a stream without — the access
 * descriptor and the admission ticket.
 *
 * Neither retried, so a single transient failure ended the session before it
 * began: the viewer saw a dead page, not a slow one. That window is real and
 * routine — the load balancer briefly has no healthy backend whenever a pod is
 * replaced, which happens on every deploy AND on Autopilot's own eviction
 * schedule, and the resulting 502 arrives at the exact moment someone is
 * pressing play.
 *
 * ONLY TRANSPORT-LEVEL FAILURES are retried: a network error (status 0) or
 * 502/503/504. Everything else is the server's considered answer and must
 * reach the caller unchanged — retrying a 403 would fight the password gate,
 * and retrying a 429 would deepen the very rate-limit it reports.
 *
 * Safe for the ticket POST: a request that never landed minted nothing, and one
 * whose response was lost minted a short-lived ticket nobody holds. Neither
 * creates a session — that happens later, over the signalling connection.
 *
 * Three attempts over ~1.2s total, which covers a pod swap without making a
 * genuinely down backend feel like a hang.
 */
const TRANSIENT_STATUSES = new Set([502, 503, 504]);
const withTransientRetry = async (fn, attempts = 3) => {
  for (let i = 0; ; i++) {
    try {
      return await fn();
    } catch (error) {
      const status = error?.response?.status || 0;
      const transient = status === 0 || TRANSIENT_STATUSES.has(status);
      if (!transient || i >= attempts - 1) throw error;
      console.warn(`[startup] transient ${status || 'network'} error — retry ${i + 1}/${attempts - 1}`);
      await new Promise((r) => setTimeout(r, 300 * (i + 1)));
    }
  }
};

const domainLookup = (() => {
  try {
    const host = window.location.hostname.toLowerCase();
    if (!API_URL || !isCustomDomain(host)) return null;
    // The ACCESS DESCRIPTOR, not just an id. This called /projects/by-domain,
    // whose only output was a project id — the player then had to call
    // /stream/access with that id before it could paint anything, two sequential
    // round trips on exactly the pages a customer paid to brand. The descriptor
    // already carries projectId, so one call now answers both questions.
    // Retried for the same reason as the calls in authenticate(): on a custom
    // domain this is the FIRST request and the only source of the projectId, so
    // one transient 502 during a pod swap leaves the viewer with no project at
    // all. A 404 (domain genuinely not ours) still resolves null immediately —
    // only transport failures are worth asking twice about.
    const attempt = (i) =>
      fetch(`${API_URL}/stream/access/by-domain/${encodeURIComponent(host)}`)
        .then((r) => {
          if (r.ok) return r.json();
          if ((r.status === 502 || r.status === 503 || r.status === 504) && i < 2) {
            return new Promise((res) => setTimeout(res, 300 * (i + 1))).then(() => attempt(i + 1));
          }
          return null;
        })
        .catch(() => (i < 2
          ? new Promise((res) => setTimeout(res, 300 * (i + 1))).then(() => attempt(i + 1))
          : null));
    return attempt(0);
  } catch (_e) {
    return null;
  }
})();

let maxHeight;
let maxWidth;
let roomName;
let sessionToken;
let telemetryToken; // session-scoped token for posting telemetry to the logger
// Session-scoped token exchanged at /liveKit/getToken for a room grant.
//
// MODULE SCOPE, like telemetryToken — assigned when the ticket resolves. Assigning
// it does NOT re-render React, so it must never be captured as a plain prop value:
// anything constructed before the ticket lands would keep `undefined` forever.
// That is precisely what shipped — every getToken went out with no Authorization
// header while apiBase (a const) arrived fine. It is passed as a GETTER instead,
// and read at request time.
let voiceToken;
let urlPassword;
// The session the viewer can end on purpose: { sessionId, endUrl }, set when
// the stream is constructed and cleared by endSessionExplicitly. MODULE SCOPE
// for the same reason as the tokens above: the unload handlers read it at
// event time, never as a captured value.
let activeSession = null;

// Close the signalling socket with code 1000 and reason "viewer-closed". The
// library's own disconnect() also closes with 1000 but with an EMPTY reason;
// the named reason is what lets the signalling server log the exit as
// deliberate. A DROPPED socket arrives there as 1005/1006 and is held for
// 70 s in case the viewer comes back — a deliberate exit must not be.
// Closed THROUGH the library (WebRtcPlayerController.closeSignalingServer with
// allowReconnect false): it sends the same 1000 frame AND turns the library's
// own auto-reconnect off. A raw socket close left it on, and in dev mode
// (MaxReconnectAttempts 100) the library reopened the socket 2 s after the
// player's own give-up or a page leave the page survived — a hidden stream
// behind the card, or an endless refused-reconnect loop re-posting
// 'disconnected'. The raw close stays as the fallback.
// The socket: PixelStreaming.signallingProtocol (the SignallingProtocol) ->
// .transport (WebSocketTransport) -> .webSocket.
function closeSignallingExplicitly() {
  const stream = PixelStreamingUiApp?.stream;
  const ws = stream?.signallingProtocol?.transport?.webSocket;
  if (!ws || !(ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  const controller = stream?._webRtcController;
  if (controller && typeof controller.closeSignalingServer === 'function') {
    try { controller.closeSignalingServer(VIEWER_CLOSED_REASON, false); return; } catch (_) { /* fall back to the raw close */ }
  }
  try { ws.close(1000, VIEWER_CLOSED_REASON); } catch (_) { /* already closing */ }
}

// End the active session on purpose: the explicit socket close above, plus a
// sendBeacon to the signalling server's POST /session/end. The beacon is the
// belt and braces for phones: a mobile browser killing the page often never
// flushes the close frame (the server then sees 1006 and waits 70 s), but a
// beacon survives page death. A plain-string body is text/plain, so there is
// no CORS preflight to lose. The endpoint is idempotent and authenticated by
// the session's own ticket, so firing it next to the socket close is harmless.
function endSessionExplicitly() {
  const session = activeSession;
  if (!session) return;
  activeSession = null;
  closeSignallingExplicitly();
  if (!sessionToken || typeof navigator.sendBeacon !== 'function') return;
  try {
    navigator.sendBeacon(session.endUrl, JSON.stringify({ sessionId: session.sessionId, ticket: sessionToken }));
  } catch (_) { /* best effort */ }
}
// Why the ticket request failed, so the caller can tell a wrong password (403)
// apart from a brute-force lockout (429). fetchData returns a bare boolean, so
// without this every failure rendered as "Incorrect password" — including the
// lockout, which left a viewer retrying a password that was already correct and
// never learning they had to wait. Module-scope (like the tokens above) is safe
// here because it is read synchronously right after the await, then fed into
// React state; nothing renders off it directly.
let lastTicketError = null;

// Where the project wants its logo. Returns only the EDGE OFFSETS — the element
// that renders it owns position and z-index. This was duplicated at three call
// sites that each baked in a different stacking value (10000000, 100000000 and
// 1), so the logo's depth depended on which path had run last, and the 1 put it
// behind the overlay entirely.
const LOGO_OFFSETS = {
  TopLeft: { top: '12px', left: '12px' },
  TopRight: { top: '12px', right: '12px' },
  BottomLeft: { bottom: '12px', left: '12px' },
  BottomRight: { bottom: '12px', right: '12px' },
};
const logoOffsets = (position) => LOGO_OFFSETS[position] || null;

// The broker's ssoError codes, said in words a viewer can act on. Anything
// unrecognised falls back to generic wording rather than leaking a raw code —
// the codes are deliberately coarse so the IdP's own error text never reaches
// the browser.
const SSO_ERRORS = {
  access_denied: 'Your account does not have access to this stream.',
  connection_mismatch: 'Single sign-on is misconfigured for this stream. Contact the owner.',
  connection_missing: 'Single sign-on is not set up for this stream. Contact the owner.',
  project_missing: 'This stream is no longer available.',
  expired_state: 'That sign-in link expired. Please try again.',
  invalid_state: 'That sign-in attempt could not be verified. Please try again.',
  missing_code: 'Your identity provider did not complete the sign-in. Please try again.',
  idp_error: 'Your identity provider rejected the sign-in. Please try again.',
  state_unavailable: 'Sign-in is temporarily unavailable. Please try again in a moment.',
  sso_failed: 'Sign-in failed. Please try again.',
  sso_start_failed: 'Sign-in could not be started. Please try again.',
};
const ssoErrorMessage = (code) => SSO_ERRORS[code] || 'Sign-in failed. Please try again.';

// "1 minute" / "12 minutes" / "45 seconds" — a lockout measured in raw seconds
// reads like a machine talking.
const formatWait = (secs) => {
  if (secs < 60) return `${secs} second${secs === 1 ? '' : 's'}`;
  const mins = Math.ceil(secs / 60);
  return `${mins} minute${mins === 1 ? '' : 's'}`;
};
let urlSsoGrant;

// UE reports VideoEncoderAvgQP on the codec's native scale: H.264/H.265 use
// 0-51 but AV1 uses qindex 0-255, so a flawless AV1 stream would read as
// "Poor" against H.264-calibrated bands. Map AV1 onto the H.264 scale before
// the value reaches the quality indicator.
function normalizeQpToH264Scale(qp, codec) {
  if (qp <= 0) return qp;
  if ((codec || '').toUpperCase() === 'AV1') return Math.round(qp * (51 / 255));
  return qp;
}

function applyCodecQpOverride(stream, selectedCodec, primaryCodec) {
  if (!stream || !selectedCodec || !primaryCodec) return;
  if (selectedCodec.toUpperCase() === primaryCodec.toUpperCase()) return;
  const qp = QP_BY_CODEC[selectedCodec.toUpperCase()];
  if (!qp) return;
  setTimeout(() => {
    const handler = stream?._webRtcController?.streamMessageController?.toStreamerHandlers?.get('Command');
    if (!handler) return;
    handler([JSON.stringify({ 'Encoder.MinQP': qp.min })]);
    handler([JSON.stringify({ 'Encoder.MaxQP': qp.max })]);
  }, 2000);
}

// Whether ANY chat feature is enabled — the single source of truth for the
// "should we join the LiveKit room" decision (text chat sends over the room's
// data channel, voice/video send media, so any one of them needs the room).
// videoChat defaults OFF when unset, matching the VoiceChatUI prop default.
// NOTE: this is only the CONNECT decision — which UI to show is decided
// per-feature downstream (ChatPanel/VideoTilesUI/ControlPanel each check their
// own flag), not here.
const isChatEnabled = (p) => !!(p?.textChat || p?.voiceChat || (p?.videoChat ?? false));

const webIp =  process.env.REACT_APP_WEBIP;

const App = () => {
  const videoRef = useRef(null);
  const logStatsTimerRef = useRef(null);
  const logStatsProbeRef = useRef(null);
  const resizeTimerRef = useRef(null);
  const editTextModalRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);  // stream audio muted by default; user unmutes manually
  const [keyStates, setKeyStates] = useState({ t: false, five: false });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(null);
  const [isStreamConnect, setIsStreamConnect] = useState(true);
  const [newMessage, setNewMessage] = useState(0);
  const [passwordValid, setPasswordValid] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [sdkConnect, setSdkConnect] = useState(false);
  const [reconnect, setReconnect] = useState(false);
  const [showEnterPassword, setShowEnterPassword] = useState(false);
  const [showSsoLogin, setShowSsoLogin] = useState(false);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  // Epoch ms the brute-force lockout clears, 0 when not locked. Drives the
  // disabled state on the gate so a locked-out viewer cannot keep submitting.
  const [lockedUntil, setLockedUntil] = useState(0);
  // Why the last sign-in attempt failed, shown on the SSO card.
  const [ssoError, setSsoError] = useState('');
  // Set when the bootstrap stopped and the viewer is not getting a stream:
  // { title, message }. Takes precedence over every other overlay state.
  const [unavailable, setUnavailable] = useState(null);
  const [project, setProject] = useState();
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchDone, setFetchDone] = useState(false);
  const [resolutionMode, setResolutionMode] = useState();
  const [roomDisconnect, setRoomDisconnect] = useState(false);
  const [toggleMicChat, setToogleMicChat] = useState(false);
  const [roomConnect, setRoomConnect] = useState(false);
  const [xrSupport, setXRSupport] = useState(false);
  const [startChatIf, setStartChatIf] = useState();
  const [authMessage, setAuthMessage] = useState(INITIAL_AUTH_MESSAGE);
  const [negotiationStatus, setNegotiationStatus] = useState('');
  const [showTcpFallbackToast, setShowTcpFallbackToast] = useState(false);
  const [primaryCodec, setPrimaryCodec] = useState();
  const [fallbaCodec, setFallbaCodec] = useState();
  const [showUiBtn, setShowUiBtn] = useState(false);
  const [sfuChatActive, setSfuChatActive] = useState(false);
  const [showChatUI, setShowChatUI] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showChatUiMobile, setShowChatUiMobile] = useState(!isMobile);
  const [showChatUiDesktop, setShowChatUiDesktop] = useState(false);
  const [someoneTyping, setSomeoneTyping] = useState(false);
  const [avgQp, setAvgQp] = useState(-1);
  const [projectId, setProjectId] = useState(null);
  // null = no logoPosition chosen; the renderer then pins the corner default.
  // This used to initialise to {}, which is TRUTHY, so the `|| fallback`
  // below kept the empty object and the logo got `position: fixed` with no
  // top/left/right/bottom at all — planted wherever static flow left it.
  const [logoStyles, setLogoStyles] = useState(null);
  const [sfuHost, setSfuHost] = useState("false");
  const [sfuPlayer, setSfuPlayer] = useState("false");
  const sfuPlayerRef = useRef(false);
  // Local dev mode: the /dev/<devSessionId> route. The customer's own Unreal
  // Editor is the streamer. It runs the EXACT normal-player flow — same access
  // descriptor, same password/SSO gate — differing only in which endpoint mints
  // the ticket (/dev-sessions/:id/ticket) and that the build/live preconditions
  // are skipped (the editor is the source, so there is no uploaded build to
  // check). A streamer restart (4006) is treated as reconnectable. A ref mirrors
  // the state so the once-registered close handler reads the current value.
  const [devMode, setDevMode] = useState(false);
  const devModeRef = useRef(false);
  // The dev session id (dev-xxxxxx) from the /dev/<id> path, kept alongside the
  // real projectId: the ticket call targets the dev endpoint by this id and the
  // player subscribes to this streamer. Null for a normal viewer.
  const devSessionIdRef = useRef(null);
  // Pinned test session (2026-10-12): share/<projectId>?pin=<pinId>. The pin (created by an operator on the ops
  // dashboard) is sent with the ticket mint instead of a password; the backend answers with the session id it
  // recorded the pin under, and the SDK puts that id on the signalling URL so the session lands on the pinned host.
  const pinIdRef = useRef(null);
  const pinnedSessionIdRef = useRef(null);
  sfuPlayerRef.current = (sfuPlayer === "true" || sfuPlayer === true);
  const [streamerId, setStreamerId] = useState(uuidv7());
  const [disconnectMessage, setDisconnectMessage] = useState('');
  // Which ending the card draws (utils/streamerLoss CLOSE_KIND: 'closed' |
  // 'lost' | 'gave-up'), null for the generic card. Any kind turns the card's
  // button into an in-place fresh-session start (see retryFreshSession).
  const [disconnectKind, setDisconnectKind] = useState(null);
  // Detail line for a 'gave-up' card that has no close code (why the player
  // stopped waiting).
  const [disconnectDetail, setDisconnectDetail] = useState(undefined);
  // Latest retryFreshSession, for the auto-retry timer armed inside a close
  // handler that was created several renders earlier.
  const retryFreshSessionRef = useRef(null);
  // Live mirror of isStreamConnect for handleTerminateSession, which runs from
  // a message listener registered several renders earlier: false means a
  // disconnect card is up and no socket will close on the viewer's behalf.
  const isStreamConnectRef = useRef(true);
  isStreamConnectRef.current = isStreamConnect;
  // Live mirror of "the viewer is looking at a gate, not at a start in
  // progress" (password, sign-in, unavailable) for handleTerminateSession: a
  // Close there has nothing to stop; without a gate, no player and no card, a
  // fresh session is on its way and the Close must stop it.
  const gateUpRef = useRef(false);
  gateUpRef.current = showEnterPassword || showSsoLogin || !!unavailable;

  // Live mirror of roomConnect so the comms helpers — called from the
  // once-registered stream response handler — read the CURRENT value instead
  // of a stale closure capture.
  const roomConnectRef = useRef(false);
  roomConnectRef.current = roomConnect;
  // Holds the pending disconnect teardown timer so a subsequent connect can
  // cancel it (otherwise the deferred teardown would clobber the reconnect).
  const disconnectTimerRef = useRef(null);

  const openChatUiMobile = () => setShowChatUiMobile(true);
  const closeChatUiMobile = () => setShowChatUiMobile(false);
  const openChatUiDesktop = () => setShowChatUiDesktop(true);
  const closeChatUiDesktop = () => setShowChatUiDesktop(false);
  const handleMicChat = () => setToogleMicChat(prev => !prev);

  const loadingText = (stage) => {
    if (sfuPlayerRef.current) return SFU_LOADING_TEXT[stage];
    return {
      one:   branding.loadingTextOne,
      two:   branding.loadingTextTwo,
      three: branding.loadingTextThree,
    }[stage];
  };

   const startSsoLogin = () => {
    setSsoError('');   // a fresh attempt shouldn't carry the last one's error
    // The normal player carries projectId in the PATH, so a pathname-only returnTo
    // preserves it across the SSO round trip. Dev carries it in ?p=, which pathname
    // drops — so re-add it for dev, else the post-SSO reload cannot bootstrap. The
    // broker appends ?ssoGrant via URLSearchParams, so an existing query is kept.
    const base = window.location.origin + window.location.pathname;
    const returnTo = devModeRef.current ? `${base}?p=${encodeURIComponent(projectId)}` : base;
    window.location.href = `${API_URL}/stream/sso/${projectId}/start?returnTo=${encodeURIComponent(returnTo)}`;
  };

  /**
   * Which project does this hostname belong to?
   *
   * GET /stream/access/by-domain/:domain returns the same descriptor the id route
   * does — projectId, mode, status, ready, branding — so the pre-auth screen can be
   * painted from this one call. It matches on a VERIFIED custom domain, so pointing
   * DNS at us does not claim someone else's project.
   *
   * Uses API_URL rather than a literal host. main hardcoded
   * https://api.streampixel.io, which meant staging could never resolve a domain
   * and an on-prem deployment called the vendor's control plane. It also used the
   * /pixelStripeApi prefix, which now survives only for the API-key surface.
   */
  const resolveProjectByDomain = async (fallbackId) => {
    try {
      // AWAITS THE REQUEST ALREADY IN FLIGHT — see `domainLookup` at module scope.
      // Starting a second one here would throw away the head start and double the
      // load on an endpoint every viewer of a custom domain hits.
      const access = domainLookup ? await domainLookup : null;
      const id = access && access.projectId;
      if (!id) throw new Error('no project for this domain');
      // No parentUrl state to set — main kept the resolved host in state, this
      // branch derives embedderOrigin from getParentFrameUrl() where it is used.
      // Setting it here would be a ReferenceError, and the domain is not needed
      // again once it has produced an id.
      setProjectId(id);
    } catch (error) {
      // FALLS BACK TO THE PATH ID, which is the behaviour worth keeping from main.
      // A domain lookup failing does not mean the link is bad: the API may be
      // briefly unreachable, and a custom domain can carry a project id in its path
      // as well. Treating every failure as fatal turns a transient blip into a dead
      // player for every viewer on that domain at once.
      console.error('Could not resolve a project for this domain:', error);
      if (fallbackId) {
        setProjectId(fallbackId);
        return;
      }
      // A 404 (nobody has verified this domain) and a network failure are shown
      // identically on purpose. A viewer can act on neither, and distinguishing
      // them would tell an unauthenticated stranger which domains are registered.
      setStatus('invalidurl');
      setUnavailable({
        title: 'This stream is unavailable',
        message: 'The link may be incorrect, expired, or no longer active.',
      });
    }
  };

  // iPhone has no element-fullscreen API, so the project's fullscreen button cannot work there:
  // ControlPanel hides it and this swipe-up prompt takes its place (same project setting, showBtn).
  useEffect(() => {
    if (!project?.showBtn) return undefined;
    return installIosSafariBars();
  }, [project?.showBtn]);

  useEffect(() => {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const baseId = pathSegments[pathSegments.length - 1];
    const isDev = pathSegments[0] === 'dev' && pathSegments.length >= 2;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const pwdValue = urlSearchParams.get('pwd');
    const ssoGrantValue = urlSearchParams.get('ssoGrant');
    // The broker sends the viewer back with ?ssoError=<code> when sign-in fails.
    // Nothing read it, so a rejected login landed back on the "Sign in" button
    // with no explanation and the viewer simply clicked it again.
    const ssoErrorValue = urlSearchParams.get('ssoError');
    const pinValue = urlSearchParams.get('pin');
    if (pinValue && /^pin-[a-f0-9]{24}$/.test(pinValue)) pinIdRef.current = pinValue;
    if (ssoErrorValue) setSsoError(ssoErrorMessage(ssoErrorValue));

    for (const [key, value] of urlSearchParams.entries()) {
      // Auth params are never forwarded into the Unreal message payload. `p` is
      // the dev link's projectId (reserved), so it is excluded on the dev route.
      if (key !== 'pwd' && key !== 'ssoGrant' && key !== 'ssoError' && key !== 'pin' && !(isDev && key === 'p')) { launchParams[key] = value; ueMessage[key] = value; }
      if (key === 'streamerId') { setStreamerId(value); streamerIdFromUrl = true; }
      if (key === 'sfuHost') setSfuHost(value);
      if (key === 'sfuPlayer') setSfuPlayer(value);
    }

    // Password / SSO-grant from the URL, captured BEFORE the dev branch so both
    // the normal and dev flows pick them up (the dev branch returns early). The
    // gate in authenticate()/fetchData() consumes whichever is present.
    if (pwdValue && baseId) {
      urlPassword = pwdValue;
      setEnteredPassword(pwdValue);
    }
    if (ssoGrantValue && baseId) {
      urlSsoGrant = ssoGrantValue;
    }

    // LOCAL DEV MODE: /dev/<devSessionId>?p=<projectId>. The customer's own Unreal
    // Editor is the streamer, viewed through the SAME player flow as a published
    // build — no synthesized config, no pre-shared token. We only capture the two
    // ids here and hand off to the normal authenticate() by setting projectId; the
    // dev-specific parts (skip the build/live preconditions, mint the ticket at
    // /dev-sessions/:id/ticket, subscribe to devSessionId) are small branches keyed
    // off devModeRef inside that shared path. The ticket is issued AFTER the
    // password/SSO gate, exactly like the normal player.
    if (isDev) {
      devModeRef.current = true;
      setDevMode(true);
      const devSessionId = baseId; // dev-xxxxxx
      const pid = urlSearchParams.get('p');
      if (!pid) {
        setStatus('invalidurl');
        setUnavailable({
          title: 'This dev link is incomplete',
          message: 'Start the session again from the dashboard.',
        });
        return;
      }
      devSessionIdRef.current = devSessionId;
      setStreamerId(devSessionId); // the dev editor registers under devSessionId
      setProjectId(pid);           // drives authenticate() exactly like a normal link
      return;
    }

    showLoadingSpinner();

    // SERVED FROM A HOST THAT IS NOT OURS MEANS A CUSTOM DOMAIN.
    //
    // 41 projects work this way: DNS for play.theirbrand.com points at the player,
    // so window.location.host IS the customer's domain and identifies the project.
    // The id may be absent from the path entirely.
    //
    // The trigger is the HOST, not a missing id — a custom domain can carry a path
    // too. And the host comes from window.location, NOT the parent frame: the page
    // is being served on that domain, so its own location is the authority. Reading
    // the embedder would answer a different question.
    //
    // This existed on main and a "remove unused files" refactor dropped it, turning
    // all 41 into "This stream is unavailable" — indistinguishable from a genuinely
    // broken link, which is why it could go unnoticed.
    if (isCustomDomain(window.location.hostname.toLowerCase())) {
      resolveProjectByDomain(baseId);
      return;
    }

    if (!baseId) {
      setStatus('invalidurl');
      setUnavailable({
        title: 'This stream is unavailable',
        message: 'The link may be incorrect, expired, or no longer active.',
      });
      return;
    }

    setProjectId(baseId);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      if (isModalVisible) setIsModalVisible(false);
    };
    if (isModalVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModalVisible]);

  const getStats = () => {
    const statsElement = PixelStreamingUiApp?.statsPanel?._statsResult;
    if (!statsElement) return {};
    const statDivs = statsElement.querySelectorAll('div');
    if (!statDivs.length) return {};

    const result = {};
    statDivs.forEach((div) => {
      const text = div.textContent.trim();
      if (!text) return;
      const colonIdx = text.indexOf(':');
      if (colonIdx === -1) return;
      const key = text.slice(0, colonIdx).trim();
      const value = text.slice(colonIdx + 1).trim();
      if (key && value && key.toLowerCase() !== 'players') {
        result[key] = value;
      }
    });
    return result;
  };

  // While the stats modal is open, refresh the scraped stats once per second so
  // the panel updates live instead of showing a frozen snapshot from open-time.
  useEffect(() => {
    if (!isModalVisible) return;
    setStats(getStats());
    const id = setInterval(() => setStats(getStats()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible]);

  useEffect(() => {
    // Both normal and dev viewers run authenticate(): dev sets projectId (from ?p=)
    // in the bootstrap above and then takes the same access → gate → ticket path,
    // branching only on devModeRef where it must.
    if (projectId) authenticate();
  }, [projectId]);

  // End the bootstrap with a reason the viewer can read, and make sure nothing
  // downstream keeps spinning: the loading UI, the password gate and the SSO card
  // are all cleared, so exactly one thing is on screen.
  const stopWith = (title, message) => {
    setUnavailable({ title, message });
    setShowEnterPassword(false);
    setShowSsoLogin(false);
    setLoading(false);
    hideLoadingSpinner();
    window.parent.postMessage({ type: "stream-state", value: "unavailable", title, message }, "*");
  };

  const authenticate = async () => {
    try {
      // REUSES THE DESCRIPTOR ALREADY FETCHED on a custom domain, instead of
      // asking for the same object a second time.
      //
      // Without this the merge saves nothing: the by-domain call returns the full
      // descriptor and then this would fetch it again by id — two round trips,
      // which is precisely what collapsing the endpoints was meant to remove.
      //
      // The projectId comparison is what makes reuse safe. It is the id the
      // descriptor itself reported, so the two can only differ if something
      // reassigned projectId in between, and falling through to a fresh fetch is
      // the right answer then.
      const prefetched = domainLookup ? await domainLookup : null;
      const data = (prefetched && prefetched.projectId === projectId)
        ? prefetched
        : (await withTransientRetry(() => axios.get(`${API_URL}/stream/access/${projectId}`))).data;

      // `ready` was returned by /stream/access all along and never read here, so
      // a project with no published build still got a password prompt, an SSO
      // round trip, or a ticket request — all of which can only end in a 409.
      const { mode, status: projectStatus, ready, branding: brandingData = {} } = data;

      // Branding first: whatever we show next — a gate, "offline", "no build" —
      // should already be wearing the customer's logo and banner.
      setBranding(brandingData);
      // Dev previews the editor regardless of the project's live switch or whether
      // a build was ever published, so it is always "active" and skips the two
      // preconditions below. Everything else — branding, the gate — is identical.
      setStatus((devModeRef.current || projectStatus) ? 'active' : 'inactive');
      if (brandingData.theme === "Dark") setDarkMode(true);

      setLogoStyles(logoOffsets(brandingData.logoPosition));

      // Two preconditions, in order, before anything else happens. Neither was
      // checked here: a switched-off or buildless project still got a password
      // prompt or a full SSO round trip, and the viewer only discovered it was
      // pointless after authenticating — the ticket call is the thing that 409s.
      // Dev skips BOTH: the customer's editor is the source, so there is no live
      // switch to honour and no uploaded build to require.
      //
      // 1. Is the stream switched on at all?
      if (!devModeRef.current && projectStatus !== true) {
        stopWith('This stream is offline', 'The project owner has turned it off. Check back later.');
        return;
      }
      // 2. Is there a build to stream? `ready` is hasValidApp() server-side.
      if (!devModeRef.current && ready === false) {
        stopWith('This stream is unavailable', 'No live build has been published yet.');
        return;
      }

      // An operator's pinned test session, admitted by the dashboard (P-40,
      // utils/admissionHandover.js): the pin IS the admission, so neither the
      // project's gate nor a ticket mint of our own. Used once; afterwards the
      // page says to start a new test instead of minting an ordinary session.
      if (!devModeRef.current && (handedOver || handoverUsed)) {
        const handover = handedOver;
        handedOver = null;
        if (!handover || handoverUsed) {
          stopWith('This test session has ended', 'Start a new test from the dashboard.');
          return;
        }
        if (handover.error || handover.projectId !== projectId) {
          stopWith('This test link cannot be used', 'Start the test again from the dashboard.');
          return;
        }
        handoverUsed = true;
        usingHandedTicket = true;
        applyTicketResponse(handover, projectId, { handedOver: true });
        return;
      }

      if (mode === "password") {
        const remembered = urlPassword || rememberedPassword(projectId);
        if (remembered) {
          handlePasswordSubmit(null, remembered, { silent: !urlPassword });
        } else {
          // A fresh session that lands on the gate is a gate interaction from
          // here on: the viewer's own submit gets the gate's messages.
          retryingAfterLoss = false;
          message = { type: "stream-state", value: "showPassword" };
          window.parent.postMessage(message, "*");
          setShowEnterPassword(true);
          setLoading(false);
        }
      } else if (mode === "sso") {
        if (urlSsoGrant) {
          const grant = urlSsoGrant;
          urlSsoGrant = undefined;
          const success = await fetchData({ ssoGrant: grant });
          if (success) {
            setShowSsoLogin(false);
            setLoading(true);
            return;
          }
          // The grant is single-use and short-lived, so the usual causes are a
          // reloaded callback URL or a stale link. Say that, instead of dropping
          // the viewer back on the button with no explanation.
          setSsoError(
            lastTicketError?.status === 503
              ? 'Sign-in is temporarily unavailable. Please try again in a moment.'
              : 'That sign-in link has already been used or expired. Please sign in again.'
          );
        }
        retryingAfterLoss = false;
        message = { type: "stream-state", value: "showSsoLogin" };
        window.parent.postMessage(message, "*");
        setShowSsoLogin(true);
        setLoading(false);
        hideLoadingSpinner();
      } else {
        fetchData();
      }
    } catch (error) {
      console.error('Error fetching stream access:', error);
      if (retryingAfterLoss && isRetryableTicketFailure(error?.response?.status)) {
        showRetryFailed();
        return;
      }
      stopWith(
        'This stream is unavailable',
        'The link may be incorrect, expired, or no longer active.'
      );
    }
  };

  // The Retry / "Start again" could not get a new session for a reason a later
  // press can fix (offline, 429, 5xx): back to the card the viewer came from —
  // "Connection lost" after a loss or a give-up, "Stream closed" after a close
  // — button intact. A refused or withdrawn stream still goes to stopWith.
  const showRetryFailed = () => {
    retryingAfterLoss = false;
    // The viewer closed the stream while this start was in flight: the
    // "Stream closed" card is up and stays (handleTerminateSession).
    if (closeRequested) { retryFromKind = null; return; }
    const kind = retryFromKind || CLOSE_KIND.LOST;
    retryFromKind = null;
    setDisconnectMessage(kind === CLOSE_KIND.LOST
      ? composeStreamerLostMessage({ retryFailed: true })
      : retryFailedMessage(kind));
    setDisconnectKind(kind);
    setDisconnectDetail(undefined);
    setIsStreamConnect(false);
    setFetchDone(false);
    setLoading(false);
    hideLoadingSpinner();
    window.parent.postMessage({
      type: 'stream-state', value: 'disconnected', reason: 'retry-failed',
      ...(kind === CLOSE_KIND.LOST ? { disconnectCode: 4006 } : {}),
    }, '*');
  };

  // The viewer asked for the close at a moment when no socket was open to
  // close on their behalf, and the Close cancelled something — the one
  // automatic retry, a reconnect waiting out its delay, a fresh session being
  // started (utils/streamerLoss terminateAction 'closed-card') — or the
  // page-leave path ended a reconnect wait the page then survived. No close
  // event will follow to draw the card and tell the embedder. Same card as
  // the close handler's CLOSED branch. Only 'closed' is posted: the embedder
  // already had its 'disconnected' when that socket dropped (or never
  // connected), and there is no close code.
  const showClosedByViewer = () => {
    setDisconnectMessage(closedByViewerMessage({ leftPage: closedOnLeave }));
    setDisconnectKind(CLOSE_KIND.CLOSED);
    setDisconnectDetail(undefined);
    setIsStreamConnect(false);
    setReconnect(false);
    setLoading(false);
    setShowUiBtn(false);
    setShowTcpFallbackToast(false);
    hideLoadingSpinner();
    window.parent.postMessage({ type: 'stream-state', value: 'closed', reason: 'viewer-closed' }, '*');
  };

  // Everything the player takes from a ticket response — its own mint's, or the
  // one the dashboard handed over (`handedOver`). Returns true when the stream
  // can start.
  const applyTicketResponse = (data, appId, { handedOver: handedOverTicket = false } = {}) => {
      const { ticket, telemetryToken: telToken, voiceToken: vToken, config } = data;
      // Only a pinned mint carries sessionId; it is the key the scheduler reads, so it must reach the SDK unchanged.
      // A handed-over ticket (an operator's pinned test, admitted by the dashboard) always carries it.
      pinnedSessionIdRef.current = ((pinIdRef.current || handedOverTicket) && typeof data.sessionId === 'string') ? data.sessionId : null;
      telemetryToken = telToken; // sent with each telemetry post to the logger
      // Absent when the project has no chat enabled — the backend only mints it
      // then, so undefined here means "chat off", not a failure.
      voiceToken = vToken;

      console.log("CONFIG:",config);

      if (!config || config.message === 'inValidApp') {
        // authenticate() checks `ready` up front, so reaching here means the
        // build was withdrawn between that call and this one.
        stopWith('This stream is unavailable', 'No live build has been published yet.');
        setLoading(false);
        return false;
      }


      sessionToken = ticket;
      retryingAfterLoss = false;

      const {
        primaryCodec, fallbaCodec, resolution, resolutionMode,
        maxStreamQuality, startResolution, startResolutionTab,
        startResolutionMobile,
        startChat,
      } = config;
      // signallingHost is deliberately NOT destructured here: the value is read
      // straight off `project` (the same config object) where the player is
      // constructed, and a second binding only invited the two to diverge.


      roomName = appId;

      setStartChatIf(startChat);
      setProject(config);
      setResolutionMode(resolutionMode);
      setFetchDone(true);
      setPrimaryCodec(primaryCodec);
      setFallbaCodec(fallbaCodec);


      const resIndex = resolutionDef.findIndex(r => r === maxStreamQuality);
      resolutionSetting = resolutionDef.slice(0, resIndex + 1);

      const getResolutionData = (resString) => {
        const match = resString?.match(/\((\d+x\d+)\)/);
        if (!match) return null;
        const [width, height] = match[1].split('x');
        return { resolution: resString, width, height };
      };

      let resData;
      if (isMobile) resData = getResolutionData(startResolutionMobile);
      else if (isTablet) resData = getResolutionData(startResolutionTab);
      else if (isDesktop) resData = getResolutionData(startResolution);

      if (resData) {
        activeResolution = resData.resolution;
        maxWidth = resData.width;
        maxHeight = resData.height;
      }

      if (resolution === false) {
        const videoElement = document.querySelector('video');
        if (videoElement) videoElement.style.objectFit = 'contain';
      }

      return true;
  };

  const fetchData = async (extra = {}) => {
    const { projectId: overrideId, ...payload } = extra;
    const appId = overrideId || projectId;

    if (!appId) {
      console.error('fetchData called before projectId was resolved');
      stopWith('This stream is unavailable', 'The link may be incorrect, expired, or no longer active.');
      return false;
    }

    // When this page is IFRAMED on a customer's site, the signalling WebSocket
    // carries OUR origin, so a domain-restricted project would deny its own
    // legitimate embed. Tell the mint who embeds us: ancestorOrigins (the
    // top-level site; read-only, the embedder cannot forge it) with the
    // referrer's origin as the Firefox fallback. The backend signs it into the
    // ticket only when this request really comes from our player origin, and
    // signalling honours it only against the project's own allow-list — a page
    // not on that list gains nothing by embedding us.
    let embedOrigin;
    if (window.self !== window.top) {
      try {
        const ancestors = window.location.ancestorOrigins;
        embedOrigin = ancestors && ancestors.length
          ? ancestors[ancestors.length - 1]
          : (document.referrer ? new URL(document.referrer).origin : undefined);
      } catch { /* leave undefined — behaves exactly as before this feature */ }
    }

    // Dev mints its ticket at the dev endpoint — same gate (public / password /
    // SSO), no build/live precondition — and subscribes to devSessionId rather
    // than the project's regional streamer. embedOrigin is a build-only concern,
    // so it is omitted for dev. Everything downstream reads the same { config }.
    const ticketUrl = devModeRef.current
      ? `${API_URL}/dev-sessions/${devSessionIdRef.current}/ticket`
      : `${API_URL}/stream/ticket`;

    try {
      const response = await withTransientRetry(
        () => axios.post(ticketUrl, {
          projectId: appId,
          ...(embedOrigin && !devModeRef.current ? { embedOrigin } : {}),
          ...(pinIdRef.current && !devModeRef.current ? { pinId: pinIdRef.current } : {}),
          ...payload,
        })
      );

      usingHandedTicket = false;
      return applyTicketResponse(response.data, appId);
    } catch (error) {
      console.error('Error generating stream ticket:', error);
      // Keep the status/message so the password gate can distinguish 403 from
      // 429; `status: 0` means the request never reached the server.
      lastTicketError = {
        status: error?.response?.status || 0,
        message: error?.response?.data?.message || '',
        // Seconds until a brute-force lockout clears; 0 when unknown.
        retryAfter: Number(error?.response?.data?.retryAfter) || 0,
        // true only at the hard cap, where waiting really is the only option.
        hard: error?.response?.data?.hard === true,
      };
      // A Retry / "Start again" whose mint failed for a reason a later press
      // can fix puts its card back — on a password project too: the
      // remembered (or URL) password was not rejected, so it is kept and the
      // gate is not shown (handlePasswordSubmit reads `onCard`).
      if (!payload.ssoGrant && retryingAfterLoss && isRetryableTicketFailure(lastTicketError.status)) {
        lastTicketError.onCard = true;
        showRetryFailed();
        return false;
      }
      // A pinned link the platform refused (dedicated edition: a pin is redeemed
      // only by its creator, signed in, from the dashboard): say so.
      const pinRefusal = IS_ONPREM && pinIdRef.current ? pinRefusalMessage(error?.response?.data?.code) : null;
      if (pinRefusal) {
        stopWith('This test link cannot be opened here', pinRefusal);
        return false;
      }
      // The SSO branch sets its own, more specific message after this returns.
      if (!payload.password && !payload.ssoGrant) {
        stopWith('This stream is unavailable', 'The link may be incorrect, expired, or no longer active.');
      }
      return false;
    }
  };

  // Tick the lockout down so the gate re-enables itself the moment the window
  // passes — without this the viewer has to reload to discover it expired.
  useEffect(() => {
    if (!lockedUntil) return undefined;
    const id = setInterval(() => {
      const left = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (left <= 0) {
        setLockedUntil(0);
        setPasswordError('');
      } else {
        setPasswordError(`Too many attempts. Try again in ${formatWait(left)}.`);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  useEffect(() => {
    // Embedders see none of the keys pressed inside this frame (a cross-site iframe keeps its own events), so an
    // embedding page that mirrors the app's state (a Stream Deck drives the app by keyboard; the page shows which
    // view / phase / visualizer is on) is told the key name and code of each press. Never from a text field: the
    // password and chat boxes stay private. Key names only, nothing typed is reassembled.
    const isTextTarget = (el) => !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    const forwardKey = (kind, event) => {
      if (window.parent === window || isTextTarget(event.target)) return;
      window.parent.postMessage({ type: 'stream-input', kind, key: event.key, code: event.code }, '*');
    };
    const handleKeyDown = (event) => {
      forwardKey('keydown', event);
      if (event.key.toLowerCase() === 't') setKeyStates(prev => ({ ...prev, t: true }));
      if (event.key === '5') setKeyStates(prev => ({ ...prev, five: true }));
    };
    const handleKeyUp = (event) => {
      forwardKey('keyup', event);
      if (event.key.toLowerCase() === 't') setKeyStates(prev => ({ ...prev, t: false }));
      if (event.key === '5') setKeyStates(prev => ({ ...prev, five: false }));
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (keyStates.t && keyStates.five && PixelStreamingUiApp) {
      setStats(getStats());
      setIsModalVisible(prev => !prev);
    }
  }, [keyStates]);

  const handleResize = useCallback(() => {
    clearTimeout(resizeTimerRef.current);
    resizeTimerRef.current = setTimeout(() => {
      let viewportH = Math.floor(window.innerHeight * window.devicePixelRatio);
      let viewportW = Math.floor(window.innerWidth * window.devicePixelRatio);
      let divideNumber = Math.min(maxWidth / viewportW, maxHeight / viewportH, 1);
      let resW = Math.floor(divideNumber * viewportW);
      let resH = Math.floor(divideNumber * viewportH);
      if (resW % 2 !== 0) resW -= 1;
      if (resH % 2 !== 0) resH -= 1;
      handleResolution(`${resW}x${resH}wf`);
    }, 250);
  }, []);

  useEffect(() => {
    if (resolutionMode === "Dynamic Resolution Mode") {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(resizeTimerRef.current);
      };
    }
  }, [resolutionMode, handleResize]);

  const handleResolution = (resolution) => {
    if (PixelStreamingUiApp) {
      PixelStreamingUiApp.stream.emitConsoleCommand(`r.SetRes ${resolution}`);
    }
  };

  useEffect(() => {
    if (!showEnterPassword && status === "active" && fetchDone) {
      const timeoutId = setTimeout(() => {
        // The viewer closed the stream while this start was on its way: the
        // "Stream closed" card is up; build nothing behind it.
        if (closeRequested) return;
        setAuthMessage(loadingText('one'));
        message = { type: "stream-state", value: "connecting" };
        window.parent.postMessage(message, "*");
        startPlay();

        if (sfuPlayerRef.current) {
          clearTimeout(hostWaitTimer);
          hostWaitTimer = setTimeout(() => {
            setAuthMessage("Waiting for the host to join…");
          }, HOST_WAIT_MS);
        }
      }, 1000);
      return () => { clearTimeout(timeoutId); clearTimeout(hostWaitTimer); };
    }
  }, [status, showEnterPassword, fetchDone]);

  // Logger posts carry the session-scoped telemetry token so the (public,
  // token-gated) logger ingest accepts them.
  // Stable across renders (empty deps) so it never churns the effect that builds
  // the chat SDK, and it closes over MODULE scope — so calling it returns whatever
  // `voiceToken` holds NOW, not what it held when this component last rendered.
  const getVoiceToken = useCallback(() => voiceToken, []);

  const loggerPostOpts = () => ({
    timeout: 1500,
    headers: telemetryToken ? { Authorization: `Bearer ${telemetryToken}` } : {},
  });

  function logStatsStart(loadingText, diagnostics = {}) {
    const data = {
      streamerId,
      projectId,
      projectRegion: project.region,
      loadingText,
      time: new Date().toISOString(),
      geoData,
      os: osName,
      browserName,
      // Embed-aware: the customer's page when iframed, our own URL otherwise.
      domainUrl: getAttributionUrl(),
      // Keep the share URL visible when embedded so both sides are traceable.
      ...(window.self !== window.top ? { shareUrl: window.location.href } : {}),
      sdk: false,
      ...diagnostics,
    };
    axios.post(LOGGER_URL, data, loggerPostOpts())
      .catch(err => { if (err.code !== 'ECONNABORTED') console.warn('Streaming log failed:', err.message); });
  }

  function logStats() {
    const statsData = getStats?.() || {};
    const data = {
      streamerId,
      projectId,
      os: osName,
      browserName,
      device: getDeviceTypeByUserAgent(),
      projectRegion: project.region,
      FPS: statsData['Framerate'] ?? null,
      latency: statsData['Net RTT (ms)'] ?? null,
      framesDecoded: statsData['Frames Decoded'] ?? null,
      framesDrop: statsData['Frames dropped'] ?? null,
      dataReceived: statsData['Received'] ?? null,
      packetLoss: statsData['Packets Lost'] ?? null,
      videoCodec: statsData['Video codec'] ?? null,
      audioCodec: statsData['Audio codec'] ?? null,
      videoResolution: statsData['Video resolution'] ?? null,
      videoBitrate: statsData['Video Bitrate (kbps)'] ?? null,
      audioBitrate: statsData['Audio Bitrate (kbps)'] ?? null,
      duration: statsData['Duration'] ?? null,
      controlsStreamInput: statsData['Controls stream input'] ?? null,
      videoQuantizationParameter: statsData['Video quantization parameter'] ?? null,
      location: geoData,
      time: new Date().toISOString(),
    };
    axios.post(LOGGER_URL, data, loggerPostOpts())
      .catch(err => { if (err.code !== 'ECONNABORTED') console.warn('Streaming log failed:', err.message); })
      .finally(() => {
        logStatsTimerRef.current = setTimeout(logStats, 60000);
      });
  }

  // Start (or restart) the 60-second stats reporter. A short probe waits up to
  // 10 s for the stats panel to show real numbers so the first row is useful,
  // then posts whatever is there. Called from _onWebRtcConnected, NOT from
  // onVideoInitialized: that callback only fires once the browser agrees to
  // play the video, so a viewer parked on the Start overlay (Edge "Block"
  // autoplay with no gesture, session_1788698314487_7083 on 2026-09-06) sent
  // no rows at all and was indistinguishable from a broken stream. Now the
  // rows arrive from the moment WebRTC connects and FPS reads 0 until play.
  function startLogStats() {
    stopLogStats();
    let attempts = 0;
    logStatsProbeRef.current = setInterval(() => {
      attempts++;
      const statsData = getStats();
      const netRtt = statsData['Net RTT (ms)'];
      const framerate = statsData['Framerate'];
      const ready = netRtt != null && framerate != null && !isNaN(netRtt) && !isNaN(framerate);
      if (ready || attempts >= 20) {
        clearInterval(logStatsProbeRef.current);
        logStatsProbeRef.current = null;
        logStats();
      }
    }, 500);
  }

  function stopLogStats() {
    clearInterval(logStatsProbeRef.current);
    logStatsProbeRef.current = null;
    clearTimeout(logStatsTimerRef.current);
    logStatsTimerRef.current = null;
  }

  // Everything the component hung off the current stream that the library does
  // not own: the SFU recovery loop, the negotiation listener, the host-wait
  // timer, the stats reporter. Called on every disconnect and before a retry.
  function teardownStream() {
    if (sfuRecoveryLoop) { clearInterval(sfuRecoveryLoop); sfuRecoveryLoop = null; }
    if (psNegotiationListener) {
      try { window.removeEventListener('streampixel:negotiation-failed', psNegotiationListener); } catch (_) { /* never added */ }
      psNegotiationListener = null;
    }
    clearTimeout(hostWaitTimer);
    hostWaitTimer = null;
    clearTimeout(streamerWaitTimer);
    streamerWaitTimer = null;
    streamerClassifierTimers.forEach((t) => clearTimeout(t));
    streamerClassifierTimers = [];
    if (offlineListener) {
      window.removeEventListener('offline', offlineListener);
      offlineListener = null;
    }
    stopLogStats();
  }

  // Fresh session after streamer loss — the Retry button, and the one automatic
  // attempt. The old session is over server-side (its seat released when the
  // streamer went), so it is never beaconed; this is a first load again: tear
  // down the player we had and everything hung off it, mint a new ticket through
  // authenticate() — the same access → gate → ticket path, so a remembered
  // password is reused and SSO shows its sign-in card — and let the connect
  // effect build a new player when fetchDone flips. No page reload, so the
  // embedding page's state and the customer's branding stay put.
  const retryFreshSession = () => {
    clearTimeout(streamerLostRetryTimer);
    streamerLostRetryTimer = null;
    activeSession = null;
    // The card this Retry / "Start again" was pressed on: a transient mint
    // failure puts the same card back (showRetryFailed), and it decides the pin.
    retryFromKind = disconnectKind;
    viewerClosed = false;
    closedOnLeave = false;
    closeRequested = false;
    gaveUpWaiting = false;
    teardownStream();
    reconnectWindowStart = null;
    reconnecting = false;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
    const old = PixelStreamingUiApp;
    PixelStreamingApp = undefined;
    PixelStreamingUiApp = undefined;
    try { old?.stream?.disconnect(); } catch (_) { /* socket already closed */ }
    try { old?.rootElement?.remove(); } catch (_) { /* never attached */ }
    try { disposeStream?.(); } catch (_) { /* nothing registered */ }
    disposeStream = null;
    // The old player's VideoPlayer keeps window resize/orientationchange
    // listeners the library never removes (not even in destroy()). Make it
    // inert: with its video element gone, a resize returns before touching
    // anything, instead of re-laying-out and re-mapping the mouse of every
    // dead player. (What those listeners still pin — the library's own config
    // listeners, a gamepad project's beforeunload — only the library can
    // release.)
    try {
      const vp = old?.stream?._webRtcController?.videoPlayer;
      if (vp) {
        vp.onResizePlayerCallback = () => {};
        vp.onMatchViewportResolutionCallback = () => {};
        vp.onVideoInitialized = () => {};
        vp.destroy();
      }
    } catch (_) { /* nothing to release */ }
    // A pinned test session (ops "Test") keeps its pin after a give-up or a
    // Close — the pinned host is what is under test, and the new mint lands on
    // it again; after a loss the pinned host is the one that died, so the
    // retry is an ordinary session (utils/streamerLoss keepsPinOnRetry) — also
    // when a Close put "Stream closed" over the loss card. The session id
    // always comes fresh from the new mint.
    if (!keepsPinOnRetry(retryFromKind, { hostLost: streamerLostThisAttempt })) pinIdRef.current = null;
    pinnedSessionIdRef.current = null;
    if (!streamerIdFromUrl && !devModeRef.current) setStreamerId(uuidv7());
    setDisconnectKind(null);
    setDisconnectDetail(undefined);
    setDisconnectMessage('');
    setIsStreamConnect(true);
    setReconnect(false);
    setShowUiBtn(false);
    setSfuChatActive(false);
    setRoomDisconnect(false);
    setFetchDone(false);
    setLoading(true);
    // A start the viewer closed may have ended on the unavailable card behind
    // "Stream closed"; this attempt asks again.
    setUnavailable(null);
    setAuthMessage(INITIAL_AUTH_MESSAGE);
    window.parent.postMessage({ type: 'stream-state', value: 'retrying' }, '*');
    retryingAfterLoss = true;
    authenticate();
  };
  retryFreshSessionRef.current = retryFreshSession;

  const startPlay = async () => {
    let resx;
    let resy;

    // Bind diagnostic helpers with closure over appStream / startTime
    const startTime = Date.now();
    const getDiag = (retry = 0) => getWebRtcDiagnostics(PixelStreamingUiApp, startTime, retry);
    const logDiag = (event, diag = {}) => logWebRtcDiagnostics(event,telemetryToken, diag, {
      streamerId,
      projectId,
      projectRegion: project.region,
      startTime
    });


    let isVrEnabled = false;
    if (navigator.xr) {
      navigator.xr.isSessionSupported("immersive-vr").then(isSupported => {
        if (isSupported) {
          setXRSupport(true)
          isVrEnabled = true;
        };
      });
    }


    if (resolutionMode === "Fixed Resolution Mode") {
      resx = maxWidth;
      resy = maxHeight;
    }

    if (resolutionMode === "Dynamic Resolution Mode" || resolutionMode === "Crop on Resize Mode") {
      let viewportH = Math.floor(window.innerHeight * window.devicePixelRatio);
      let viewportW = Math.floor(window.innerWidth * window.devicePixelRatio);
      let maxPixel = Math.floor(maxHeight * maxWidth);
      let viewportPixel = viewportH * viewportW;

      if (maxPixel > viewportPixel) {
        resy = viewportH;
        resx = viewportW;
      } else {
        let divideNumber = Math.min(maxWidth / viewportW, maxHeight / viewportH);
        resx = Math.floor(divideNumber * viewportW);
        resy = Math.floor(divideNumber * viewportH);
      }
    }

    const { appStream, pixelStreaming, selectedCodec, sessionId: streamSessionId, dispose } = StreamPixelApplication({
      AutoPlayVideo: true,
      region: project.region,
      // Comes from the ticket response. There is no env-var fallback: the SDK
      // reads this only from settings, and the REACT_APP_*_SIGNALLING_SERVER
      // vars the comment used to cite no longer exist — the backend picks the
      // region's host so moving a region does not mean rebuilding every
      // embedding site's bundle.
      signallingHost: project.signallingHost,
      // Pinned test session: the backend's session id, so placement lands on the pinned host. Undefined otherwise.
      ...(pinnedSessionIdRef.current ? { sessionId: pinnedSessionIdRef.current } : {}),
      StartVideoMuted: true,
      AutoConnect: true,
      // NEVER hand the library a capture flag. With UseMic/UseCamera on,
      // Epic's receiveOffer() awaits getUserMedia() BEFORE createAnswer() — a
      // permission prompt the viewer ignores holds the SDP answer forever
      // (no video, no error, worker kills the session 10 min later; 14 of the
      // 16 genuine offer-never-answered sessions in the week to 2026-09-05
      // were showMic projects; the camera flag sits on the same await).
      // Mic and camera are attached after the stream is up instead — see
      // _onWebRtcSdpOffer and the post-connect block below.
      useMic: false,
      UseCamera: false,
      appId: projectId,
      unrealVersion:project?.unrealVersion || 5.2,
      primaryCodec,
      fallBackCodec: fallbaCodec,
      // Guard against a config that arrives without the field (older backend
      // or older project doc): a non-number silently disables AFK in the lib.
      afktimeout: Number(project.afktimeout) > 0 ? Number(project.afktimeout) : 600,
      touchInput: project.touchInput,
      mouseInput: project.mouseInput,
      gamepadInput: project.gamepadInput,
      resolution: project.resolution,
      hoverMouse: project.hoverMouse,
      xrInput: project.xrInput,
      streamerId,
      sfuHost,
      sfuPlayer,
      keyBoardInput: project.keyBoardInput,
      fakeMouseWithTouches: project.fakeMouseTouch,
      resX: resx,
      resY: resy,
      isVrEnabled,
      forceTurn:project.forceTurn || false,
      sessionToken,
      ticketInUrl: TICKET_IN_URL && !usingHandedTicket,
      // Dev mode: enables the lib's streamer re-poll (so the player attaches when
      // the editor comes up, and re-attaches after every restart) and turns AFK off.
      devMode: devModeRef.current,
    });

    PixelStreamingApp = pixelStreaming;
    PixelStreamingUiApp = appStream;
    disposeStream = dispose;
    // A new stream: nobody has closed it and nobody has given up on it yet.
    viewerClosed = false;
    closedOnLeave = false;
    gaveUpWaiting = false;
    retryFromKind = null;
    streamerLostThisAttempt = false;
    // The URL's launch parameters go to THIS session's Unreal instance once
    // (onVideoInitialized), not again after a reconnect inside the attempt.
    let launchParamsSent = false;
    // Everything below belongs to THIS attempt. A retry swaps PixelStreamingUiApp
    // for a new application, but nothing unregisters the old transport's close
    // listeners (ours or the library's) or the old classifier timers — so each
    // of them checks it still speaks for the attempt that is up before touching
    // the module flags, the card or the embedder. Without this, a 60 s verdict
    // armed on attempt 1 ran getDiag() against attempt 2's player (still in
    // queue, no peer connection → "not connected") and ended attempt 2 as a
    // give-up 16 s into its start.
    const isCurrentAttempt = () => !EXPLICIT_CLOSE || PixelStreamingUiApp === appStream;
    // A classifier timer is still armed when it belongs to the attempt that is
    // up AND no teardown cleared it while its diagnostics were being read.
    const classifierStillArmed = (id) => isCurrentAttempt() && streamerClassifierTimers.includes(id);
    // Explicit close target: same host as the signalling socket, over https.
    // Kept from here (not from "connected") so a tab closed during the
    // cold-start wait ends the queued/starting session too.
    activeSession = { sessionId: streamSessionId, endUrl: `https://${project.signallingHost}/session/end` };

    // The player stops waiting for a stream that is not coming. `why`:
    // 'streamer-mute' (an SDP answer, then 60 s with no media and no remote
    // candidates — the classifier below) or 'no-offer' (the backstop: nothing
    // from signalling for STREAMER_WAIT_BACKSTOP_MS). Card first, then end the
    // session on purpose — the seat is held for a streamer that never arrived —
    // and the close event that follows is recognised as this give-up
    // (gaveUpWaiting), not as a new disconnect. NEVER an automatic retry: the
    // viewer has already waited, and a retry placed on the same rebooting host
    // would only make them wait again (2026-10-23, dedicated test platform).
    const giveUpWaiting = (why) => {
      if (!isCurrentAttempt() || gaveUpWaiting || viewerClosed) return;
      gaveUpWaiting = true;
      clearTimeout(streamerWaitTimer);
      streamerWaitTimer = null;
      logDiag('gaveUpWaiting', { why, timeSinceStart: Date.now() - startTime, pinned: !!pinIdRef.current });
      teardownStream();
      setDisconnectKind(CLOSE_KIND.GAVE_UP);
      setDisconnectDetail(gaveUpDetail(why));
      setDisconnectMessage(gaveUpMessage());
      setIsStreamConnect(false);
      setLoading(false);
      setShowUiBtn(false);
      setShowTcpFallbackToast(false);
      hideLoadingSpinner();
      // Embedder order for a give-up: this 'disconnected' (reason
      // gave-up:<why>) first; the library's bare {value:'disconnected'}
      // (appStream.onDisconnect) follows when the socket closed below is
      // gone; the close handler adds nothing (gaveUpWaiting).
      window.parent.postMessage({ type: 'stream-state', value: 'disconnected', reason: `gave-up:${why}` }, '*');
      logStatsStart('Disconnected', { reason: `gave-up:${why}` });
      endSessionExplicitly();
    };
    // Armed once, here. Cleared by the first queue-position message (a queued
    // viewer is waiting on purpose; the server owns that wait and its 4005
    // once they are placed) and by the SDP offer.
    const armStreamerWait = () => {
      // An SFU guest waits for a host that may not have started streaming (the
      // server's own 3-minute rule ends that), and dev mode waits for the
      // editor indefinitely by design.
      if (sfuPlayerRef.current || devModeRef.current) return;
      clearTimeout(streamerWaitTimer);
      streamerWaitTimer = setTimeout(() => {
        streamerWaitTimer = null;
        giveUpWaiting('no-offer');
      }, STREAMER_WAIT_BACKSTOP_MS);
    };
    if (EXPLICIT_CLOSE) armStreamerWait();

    let timeTaken = 0;

    

    PixelStreamingApp.addResponseEventListener('handle_responses', handleResponseApp);

    // In production the server subscribes the player when the app boots and the
    // library's own streamer poll is off (MaxReconnectAttempts 0), so the
    // streamer list that comes back EMPTY at connect makes the library draw its
    // "GAVE UP WAITING FOR <id>. CLICK TO TRY AGAIN" overlay — a reload prompt
    // that showed through under the loading text on 2026-10-23. The player
    // owns that wait (giveUpWaiting above), so the library's verdict is hidden.
    // The library's handler is registered in its constructor and runs first.
    if (EXPLICIT_CLOSE && !devModeRef.current) {
      appStream.stream.addEventListener('streamerListMessage', ({ data } = {}) => {
        if (!data?.autoSelectedStreamerId) {
          try { appStream.hideCurrentOverlay(); } catch (_) { /* no overlay up */ }
        }
      });
    }

    const webRtcControllerForCommand = appStream.stream._webRtcController;
    if (webRtcControllerForCommand) {
      webRtcControllerForCommand.onCommand = (rawMessage) => {
        try {
          const commandAsString = new TextDecoder('utf-16').decode(rawMessage.slice(1));
          const command = JSON.parse(commandAsString);
          if (command.showOnScreenKeyboard === true) {
            showEditTextModal(command.contents, webRtcControllerForCommand);
          }
        } catch (e) {
          console.warn('Failed to parse onCommand message:', e);
        }
      };

      // Send File (UE "Send File" / "Send File as Byte Array" blueprint nodes):
      // Epic's lib already receives the chunks and reassembles them into
      // webRtcController.file, but ships the browser-download step commented out.
      // We wrap the FromStreamer file handlers so a completed transfer is handed
      // to the browser as a download. Done here in app code (not a node_modules
      // patch) so it survives the build pipeline regardless of postinstall hooks.
      const smc = webRtcControllerForCommand.streamMessageController;
      if (smc?.fromStreamerHandlers) {
        const origFileExtension = smc.fromStreamerHandlers.get('FileExtension');
        const origFileContents = smc.fromStreamerHandlers.get('FileContents');
        // A new transfer always starts with the extension message; clear the
        // per-file download guard there so multiple files each download once.
        smc.fromStreamerHandlers.set('FileExtension', (data) => {
          origFileExtension?.(data);
          const f = webRtcControllerForCommand.file;
          if (f) { f.spDownloaded = false; f.spReceivedBytes = 0; f.spStamp = null; }
          spRemoveFileCard(); // a new transfer replaces any prior card
        });
        smc.fromStreamerHandlers.set('FileContents', (data) => {
          origFileContents?.(data); // let the lib append the chunk + mark valid
          const f = webRtcControllerForCommand.file;
          if (!f || !Array.isArray(f.data) || !f.chunks) return;
          const last = f.data[f.data.length - 1];
          f.spReceivedBytes = (f.spReceivedBytes || 0) + (last ? (last.byteLength || last.length || 0) : 0);
          if (f.data.length < f.chunks) {
            updateFileProgress(f.data.length, f.chunks, spFileName(f), f.spReceivedBytes);
          } else if (f.valid && !f.spDownloaded) {
            f.spDownloaded = true;
            try {
              const blob = new Blob([...f.data], { type: f.mimetype || 'application/octet-stream' });
              deliverReceivedFile(blob, spFileName(f), f.mimetype);
            } catch (e) {
              console.error('[streampixel] file download failed', e);
            }
          }
        });
      }
    }



    const showEditTextModal = (text, controller) => {
      if (editTextModalRef.current?.rootElement) {
        editTextModalRef.current.rootElement.remove();
      }
      const modal = new EditTextModal();
      editTextModalRef.current = modal;
      document.body.appendChild(modal.rootElement);
      Object.assign(modal.rootElement.style, { top: '45%' });
      modal.showModal(text);
      modal.events.addEventListener('editConfirmed', (e) => {
        controller.streamMessageController
          .toStreamerHandlers.get('TextboxEntry')?.([e.detail]);
      });
    };

    pixelStreaming._onWebRtcSdpOffer = async () => {
      // This fires after setRemoteDescription(offer) and BEFORE createAnswer(),
      // so the offer's audio m-line already exists on the peer connection.
      // Mark it sendrecv now (synchronously — before the first await) so the
      // answer tells UE "I may send audio" while sending nothing; the track is
      // attached post-connect with replaceTrack, which needs no renegotiation.
      // Nothing here can wait on a permission prompt.
      if (project.showMic || project.showCamera) {
        try {
          const pc = appStream.stream._webRtcController?.peerConnectionController?.peerConnection;
          for (const t of pc?.getTransceivers() ?? []) {
            const kind = t.receiver?.track?.kind;
            const wanted = (kind === 'audio' && project.showMic) || (kind === 'video' && project.showCamera);
            if (wanted && t.direction !== 'sendrecv') t.direction = 'sendrecv';
          }
        } catch (e) {
          console.warn('capture: could not mark the media line sendrecv', e);
        }
      }
      message = { type: "stream-state", value: "Starting connection to Streampixel server, please wait" };
      window.parent.postMessage(message, "*");
      clearTimeout(hostWaitTimer);
      // The streamer is there: the pre-offer backstop is over, the post-answer
      // classifier below takes the watch.
      clearTimeout(streamerWaitTimer);
      streamerWaitTimer = null;
      setAuthMessage(loadingText('one'));
      logStatsStart("connecting");
      const diagnostics = await getDiag();
      logDiag("sdpOffer", diagnostics);
    };

    pixelStreaming._onWebRtcSdpAnswer = async () => {
      message = { type: "stream-state", value: "Almost there, hold tight- awesomeness loading" };
      window.parent.postMessage(message, "*");
      setAuthMessage(loadingText('two'));
      logStatsStart("Almost there, hold tight- awesomeness loading");
      const diagnostics = await getDiag();
      if (!isCurrentAttempt()) return;
      logDiag("sdpAnswer", diagnostics);

      if (!EXPLICIT_CLOSE) {
        // SaaS: the classifier exactly as it was — the network explanations and the mute
        // streamer's "refresh the page" are shown as text at +60 s, and nothing ends the attempt.
        setTimeout(async () => {
          const d = await getDiag();
          if (d.iceConnectionState !== 'connected' &&
            d.iceConnectionState !== 'completed' &&
            d.connectionState !== 'connected') {
            // Classify WHY the connection never came up, from the same
            // diagnostics we log. Order matters (validated against 20 days of
            // production timeouts): a client that gathered nothing often ALSO
            // sees zero remotes — its broken network must win over "streamer
            // mute". Anything ambiguous falls through to the generic message.
            const remotes = Array.isArray(d.remoteCandidates) ? d.remoteCandidates.length : d.remoteCandidates;
            const bytes = d.transport ? d.transport.bytesReceived : null;
            let failureClass = 'unclassified';
            let failureMessage = null;
            if (d.turnCandidatesFound === false && d.srflxCandidatesFound === false) {
              failureClass = 'nothing-gathered';
              failureMessage = 'Your network is blocking the connection entirely. Please try a different network.';
            } else if (remotes === 0) {
              failureClass = 'streamer-mute';
              failureMessage = 'The stream did not respond. Please refresh the page to try again.';
            } else if ((d.candidatePairs || 0) > 0 && (d.connectedPairs || 0) === 0 && bytes === 0 && d.turnCandidatesFound === true) {
              failureClass = 'network-blackhole';
              failureMessage = 'Your network appears to block video streaming traffic. Try a different network — a mobile hotspot usually works — or ask IT to allow WebRTC.';
            } else if (bytes > 0) {
              failureClass = 'progressed-then-stalled';
            }
            logDiag("timeout", { timeSinceStart: Date.now() - startTime, failureClass, ...d });
            // SHOW the explanation at 60s, not 30s. 20 days of history: of 437
            // timeouts, 37 (8.5%) still connected within the next 30-60s (slow
            // ICE / the auto-reconnect landing) — at 60s the premature-message
            // rate drops to ~3% while the 31% who will only ever recover by
            // refreshing (median 6.5 MINUTES later without guidance) get told
            // what to do. Logging stays at 30s so telemetry stays comparable
            // with history. The message never kills the attempt — if the
            // connection lands afterwards, the stream simply takes over.
            if (failureMessage) {
              setTimeout(async () => {
                const d2 = await getDiag();
                if (d2.iceConnectionState !== 'connected' &&
                  d2.iceConnectionState !== 'completed' &&
                  d2.connectionState !== 'connected') {
                  setAuthMessage(failureMessage);
                }
              }, 30000);
            }
          }
        }, 30000);
        return;
      }

      // Both timers below are tracked (teardownStream clears them) AND check,
      // before and after reading diagnostics, that they are still armed for
      // the attempt that is up: getDiag reads the module-scope player, so a
      // verdict left over from a previous attempt (a Close or a loss, then
      // "Start again" / the auto-retry inside 60 s) would otherwise judge the
      // new one — still in queue, no peer connection, "not connected" — and
      // end it as a give-up.
      const classifierLog = setTimeout(async () => {
        if (!classifierStillArmed(classifierLog)) return;
        const d = await getDiag();
        if (!classifierStillArmed(classifierLog)) return;
        if (!isConnectionUp(d)) {
          // Classify WHY the connection never came up, from the same
          // diagnostics we log (utils/streamerLoss classifyConnectTimeout:
          // nothing-gathered, streamer-mute, network-blackhole,
          // progressed-then-stalled, unclassified — order validated against
          // 20 days of production timeouts).
          const first = classifyConnectTimeout(d);
          logDiag("timeout", { timeSinceStart: Date.now() - startTime, failureClass: first.failureClass, ...d });
          // SHOW the explanation at 60s, not 30s. 20 days of history: of 437
          // timeouts, 37 (8.5%) still connected within the next 30-60s (slow
          // ICE / the auto-reconnect landing) — at 60s the premature-message
          // rate drops to ~3% while the 31% who will only ever recover by
          // refreshing (median 6.5 MINUTES later without guidance) get told
          // what to do. Logging stays at 30s so telemetry stays comparable
          // with history. The network explanations never kill the attempt —
          // if the connection lands afterwards, the stream simply takes over.
          // 'streamer-mute' is different: the streamer answered and then sent
          // nothing, which is a host that is not coming (rebooting, or the app
          // died before media). That one used to end as dead-end text ("The
          // stream did not respond. Please refresh the page to try again.")
          // with no button; it is now the player's own give-up — the
          // "Connection lost" card with a Retry that starts a fresh session —
          // but only if the streamer is STILL mute at +60 s: remote candidates
          // that arrived in between (a slow TURN allocation on the host) mean
          // ICE may yet connect, and ending that session would kill a stream
          // seconds from starting (connectTimeoutVerdict).
          if (armsConnectVerdict(first)) {
            const classifierVerdict = setTimeout(async () => {
              if (!classifierStillArmed(classifierVerdict)) return;
              const d2 = await getDiag();
              if (!classifierStillArmed(classifierVerdict)) return;
              const verdict = connectTimeoutVerdict(first, d2);
              if (verdict.action === 'give-up') giveUpWaiting('streamer-mute');
              else if (verdict.action === 'message') setAuthMessage(verdict.message);
            }, 30000);
            streamerClassifierTimers.push(classifierVerdict);
          }
        }
      }, 30000);
      streamerClassifierTimers.push(classifierLog);
    };

    pixelStreaming._onWebRtcConnected = async () => {
      setAuthMessage(loadingText('three'));
      message = { type: "stream-state", value: "finalising" };
      window.parent.postMessage(message, "*");
      logStatsStart("Sharpening pixels and buffing the details...");
      const diagnostics = await getDiag();

      const tcpAtConnect = await isTcpRelayActive(appStream);
      // Selected-path attribution (host/srflx/relay + which TURN provider) —
      // the per-session record the TURN cost work reads. See getSelectedPathInfo.
      const pathInfo = await getSelectedPathInfo(appStream);
      logDiag("connected", { ...diagnostics, tcpFallback: tcpAtConnect, pathInfo });
      startLogStats();
      // Project toggle (showUdpBlockedWarning, default true) controls only the
      // VISIBLE toast — tcpFallbackDetected telemetry always logs, so our own
      // diagnostics keep seeing TCP-relay sessions on opted-out projects.
      const udpWarnEnabled = project.showUdpBlockedWarning !== false;
      if (tcpAtConnect) {
        if (udpWarnEnabled) setShowTcpFallbackToast(true);
        logDiag("tcpFallbackDetected", { when: "connected" });
      } else {
        setTimeout(async () => {
          if (await isTcpRelayActive(appStream)) {
            if (udpWarnEnabled) setShowTcpFallbackToast(true);
            logDiag("tcpFallbackDetected", { when: "+5s" });
          }
        }, 5000);
      }
    };

    appStream.stream._onVideoEncoderAvgQP = (qp) => setAvgQp(normalizeQpToH264Scale(qp, selectedCodec));

    try {
      const webRtcCtrl = appStream.stream._webRtcController;
      if (webRtcCtrl?.protocol) {
        import('@epicgames-ps/lib-pixelstreamingcommon-ue5.7').then(({ Messages }) => {
          webRtcCtrl.protocol.addListener(Messages.config.typeName, (configMsg) => {
            const opts = configMsg.peerConnectionOptions;
            const iceServers = opts?.iceServers || [];
            if (!iceServers.length) {
              console.error('⚠️ CRITICAL: Config message has EMPTY/MISSING iceServers!', { opts });
              logDiag("configMessageMissingIceServers", {
                error: "Config message missing ICE servers",
                peerConnectionOptions: opts, iceServers,
              });
            } else {
              logDiag("configMessageReceived", {
                iceServersCount: iceServers.length,
                iceServers: iceServers.map(s => ({ urls: s.urls, hasCreds: !!(s.username || s.credential) })),
              });
            }
          });
        }).catch(e => console.warn('Failed to load pixelstreamingcommon:', e));
      }
    } catch (e) {
      console.warn('Failed to intercept config message:', e);
    }

    try {
      const wsCtrl = appStream.stream.signallingProtocol;
      if (wsCtrl?.transport) {
        if (wsCtrl.isConnected()) {
          setIsMuted(true);
          setRoomConnect(false);
          setToogleMicChat(false);
          logDiag("signalingServerConnected", {
            initialState: true,
            timestamp: new Date().toISOString(),
            timeSinceStart: Date.now() - startTime,
          });
        }
        wsCtrl.transport.on('open', () => {
          logDiag("signalingServerConnected", {
            timestamp: new Date().toISOString(),
            timeSinceStart: Date.now() - startTime,
          });
        });
        wsCtrl.transport.on('error', (error) => {
          console.error('❌ Signaling server error:', error);
          // The browser's WebSocket error event carries no detail by spec, so
          // `error` is usually undefined — log the context that explains the
          // error instead: offline blip vs hidden/suspended tab vs socket state.
          // The paired close event (below) carries the code/reason.
          logDiag("signalingServerError", {
            error: error?.message || String(error),
            wsReadyState: wsCtrl.transport?.webSocket?.readyState,
            online: navigator.onLine,
            visibility: document.visibilityState,
            timeSinceStart: Date.now() - startTime,
          });
        });
      }
    } catch (e) {
      console.warn('Failed to log signaling server events:', e);
    }

    try {
      const webRtcCtrl2 = appStream.stream._webRtcController;
      if (webRtcCtrl2?.peerConnection) {
        const pc = webRtcCtrl2.peerConnectionController?.peerConnection;
        pc.addEventListener('iceconnectionstatechange', async () => {
          if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
            const diagnostics = await getDiag();
            logDiag("iceFailure", {
              failureState: pc.iceConnectionState,
              timeSinceStart: Date.now() - startTime,
              ...diagnostics,
            });
          }
        });
        pc.addEventListener('connectionstatechange', async () => {
          if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
            const diagnostics = await getDiag();
            logDiag("connectionFailure", {
              failureState: pc.connectionState,
              timeSinceStart: Date.now() - startTime,
              ...diagnostics,
            });
          }
        });
      }
    } catch (_) { /* don't break connection if logging fails */ }

    const wsController = appStream.stream.signallingProtocol;
    const isConnected = wsController.isConnected();

    if (isConnected) {
      wsController.transport.on('message', async (msgRaw) => {
        // The transport outlives a Retry (nothing unregisters this listener):
        // only the attempt that is up may touch the card, the backstop or the
        // embedder.
        if (!isCurrentAttempt()) return;
        try {
          if (msgRaw.message === "locationData") {
            geoData = {
              country: msgRaw.data.country,
              countryCode: msgRaw.data.country_code,
              state: msgRaw.data.state,
              city: msgRaw.data.city,
              latitude: msgRaw.data.latitude,
              longitude: msgRaw.data.longitude,
              zip: msgRaw.data.zip,
            };
            const diagnostics = await getDiag();
            logDiag("initialConnection", diagnostics);
            logStatsStart("Starting connection to Streampixel server, please wait");
          }

          if (msgRaw.message === "You are in Queue") {
            const msgFormat = `${msgRaw.message} ${msgRaw.position}`;
            setAuthMessage(msgFormat);
            setLoading(true);
            window.parent.postMessage({ type: "stream-state", value: `queue-${msgRaw.position}` }, "*");
            logStatsStart(msgRaw.message);
            // Queued on purpose: the player's backstop is OFF from here, not
            // re-armed. Positions are only re-sent when a seat is released, so
            // a viewer whose position does not move for minutes (account-CCU
            // cap full, long sessions) is waiting, not stuck — ending their
            // session would also cost them their place. Once placed, the
            // server's own 180 s wait (4005) covers a streamer that never comes.
            clearTimeout(streamerWaitTimer);
            streamerWaitTimer = null;
          }
        } catch (error) {
          console.error("Failed to parse message:", error);
        }
      });

      // AFK: the SDK shows the countdown overlay, but when the countdown
      // EXPIRES the lib just tears the connection down — the viewer then saw a
      // bare "Disconnected code:NNNN." with no hint they idled out, and the
      // close handler treated the deliberate idle-kick like a network blip and
      // tried to reconnect. Catch the lib's own expiry event so the goodbye
      // says why and no pointless reconnect is attempted.
      let afkTimedOut = false;
      appStream.stream.addEventListener('afkTimedOut', () => {
        afkTimedOut = true;
      });

      // Playback telemetry. The lib refuses to fire onVideoInitialized until the
      // browser accepts play(); when the browser refuses (Edge "Block" autoplay,
      // no user gesture because the password came from the URL) it raises
      // playStreamRejected and shows the Start overlay instead. Nothing recorded
      // either outcome, so a viewer who never pressed Start looked like a broken
      // stream. Two rows make the timeline explicit: rejected (with the DOM
      // error name) and initialized (playback really began).
      appStream.stream.addEventListener('playStreamRejected', ({ data } = {}) => {
        const reason = data?.reason;
        logDiag('playStreamRejected', {
          reasonName: reason?.name ?? String(reason ?? 'unknown'),
          reasonMessage: reason?.message ?? null,
          visibility: document.visibilityState,
        });
      });
      appStream.stream.addEventListener('videoInitialized', () => {
        logDiag('videoInitialized', { visibility: document.visibilityState });
        // docs.streampixel.io "Hide / unhide touch controls": tell the Unreal app whether this viewer is on a
        // phone/tablet, on every connection and reconnect (videoInitialized fires again after a reconnect), so it
        // can pick its touch or desktop HUD. Sent once per video start: an embedder's own override is sent after
        // videoInitialized (as the docs say) and must not be overwritten. Not from an SFU guest: a room shares one
        // Unreal app, and a phone guest must not switch the HUD of everyone else in it.
        if (sfuPlayerRef.current || !isCurrentAttempt()) return;
        try {
          const msg = isTouchDeviceMessage();
          const sent = appStream.stream.emitUIInteraction(msg);
          logDiag('isTouchDeviceSent', { value: msg.value, sent: !!sent });
        } catch (_) { /* never break the stream over it */ }
      });

      wsController.transport.on('close', async (data) => {
        // The close of a socket that belonged to a PREVIOUS attempt (a give-up
        // whose echo came late, after the viewer already pressed Retry): the
        // flags, the card, the embedder and the telemetry token now belong to
        // the new attempt, and a 1006 here must not start a "Reconnecting…"
        // over it.
        if (!isCurrentAttempt()) return;
        logDiag("signalingServerDisconnected", {
          code: data.code, reason: data.reason,
          timeSinceStart: Date.now() - startTime,
        });

        setIsMuted(true);
        if (!sfuPlayerRef.current) {
          setRoomConnect(false);
          setToogleMicChat(false);
          setRoomDisconnect(true);
        }
        stopLogStats();

        // Streamer loss: the GPU host or the Unreal app went away mid-session.
        // Signalling sends 4006 "Streamer Disconnected" on every such path (a
        // closed streamer socket, or the platform ending the session underneath
        // a streamer socket that never closed). The session is already over
        // server-side: nothing to reconnect to, nothing left to end — so the
        // explicit-close beacon must NOT fire for it (activeSession is cleared
        // here, before anything else can read it), and the viewer gets a Retry
        // that starts a FRESH session. Dev mode keeps its own meaning for 4006
        // (editor restart → re-attach) via the reconnect branch below; an SFU
        // guest losing the host sees this card too — the room's fate is the
        // server's last-one-out rule, which a retry simply rejoins.
        clearTimeout(streamerWaitTimer);
        streamerWaitTimer = null;
        // SaaS (EXPLICIT_CLOSE off): every close is 'other' — the close handler's wording, reconnect rules and
        // postMessage states exactly as before; none of the kinds below can occur.
        const closeKind = EXPLICIT_CLOSE ? classifyClose({
          code: data.code, reason: data.reason,
          viewerClosed, gaveUp: gaveUpWaiting, devMode: devModeRef.current,
        }) : CLOSE_KIND.OTHER;
        const streamerLost = closeKind === CLOSE_KIND.LOST;
        // A loss, and signalling's own streamer timeout (4005), end the session
        // server-side: nothing left to beacon on unload. (The player's own
        // give-up already ended it on purpose and cleared this itself.)
        // A refused admission (4401) never started a session: nothing to end either.
        if (streamerLost || closeKind === CLOSE_KIND.GAVE_UP || closeKind === CLOSE_KIND.REFUSED) activeSession = null;
        // The player already ended this attempt itself (giveUpWaiting): the
        // card is up and the embedder has been told. This close is the echo of
        // the player's own socket close, not a new event.
        if (closeKind === CLOSE_KIND.GAVE_UP && gaveUpWaiting) return;

        let disconnectMessageText = `Disconnected code:${data.code}.`;

        const closeReasons = [
          "Project Inactive", "Worker Node Unavailable", "Application Not Found",
          "Application Error", "Maximum Runtime Reached", "No streamer connected",
          // Forced TURN with no relay to hand out: a reconnect is refused the same way.
          RELAY_UNAVAILABLE_REASON,
        ];

        // 1008 is the signalling gate REFUSING admission (ticket invalid or
        // expired, or a malformed URL) — retrying presents the same refused
        // credentials forever. An AFK expiry is a deliberate kick. Neither is
        // a network blip; neither reconnects. 4401 is the same refusal on the
        // first-message admission path (SPX_TICKET_IN_URL=false, P-39).
        // A close the viewer asked for, a loss and a give-up never reconnect
        // either: each has its own card and a fresh-session action.
        const isReconnectable =
          closeKind === CLOSE_KIND.OTHER && !afkTimedOut && data.code !== 1008 && data.code !== 4401 &&
          (data.code === 1005 || data.code === 1006 ||
          // Dev mode: a dev streamer restart closes the viewer with 4006. That is
          // the ITERATION loop, not a terminal error — reconnect and re-attach
          // when the editor comes back up.
          (devModeRef.current && data.code === 4006) ||
          (sfuPlayerRef.current && !closeReasons.includes(data.reason)));

        if (isReconnectable) {
          const now = Date.now();

          setLogoStyles(logoOffsets(project.logoPosition));

          if (!reconnectWindowStart) {
            reconnectWindowStart = now;
          }

          const elapsed = now - reconnectWindowStart;

          if (elapsed > 60000 && !sfuPlayerRef.current) {
            reconnectWindowStart = null;
            reconnecting = false;
            setAuthMessage('Disconnected');
            setReconnect(false);
            setIsStreamConnect(false);
            setShowTcpFallbackToast(false);
            setLoading(false);
            setShowUiBtn(false);
            window.parent.postMessage({ type: "stream-state", value: "disconnected", code: 4007 }, "*");
            setDisconnectMessage('Disconnected code:4007. We could not reconnect to the stream.');
            logDiag("signalingServerDisconnected", {
              code: 4007, reason: "Reconnection Failed",
              timeSinceStart: Date.now() - startTime,
            });
            return;
          }

          if (reconnecting) return;

          reconnecting = true;

          window.parent.postMessage({ type: "stream-state", value: "reconnecting" }, "*");
          logStatsStart("reconnecting");
          setAuthMessage(sfuPlayerRef.current
            ? "Waiting for the host to reconnect…"
            : 'Reconnecting…');
          setReconnect(true);
          setIsStreamConnect(true);
          setLoading(true);
          setShowUiBtn(false);

          const webRtcPlayer = appStream.stream._webRtcController;
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            // A Retry replaced this player (retryFreshSession already reset
            // `reconnecting`): no reconnect.
            if (!isCurrentAttempt()) return;
            // The page-leave path ended the session during the wait and the
            // page is still here (bfcache restore): no reconnect to a session
            // that is over — and no silent stand-down either, which left the
            // viewer on a "Reconnecting…" spinner with no button. (A Close
            // during the wait cancels this timer and draws the card itself.)
            if (viewerClosed) {
              reconnecting = false;
              showClosedByViewer();
              return;
            }
            webRtcPlayer.connectToSignallingServer();
            reconnecting = false;
          }, 5000);
        }else{

        // Viewer-facing wording for the platform's close codes. 4006 (the most
        // common mid-session failure) previously fell through every branch and
        // showed a bare "Disconnected code:4006." with no explanation or next
        // step. Wording is deliberately neutral: 4006 covers both a crash and
        // an intentional in-app close — the code cannot distinguish them, so
        // neither does the message.
        const friendlyCloseMessages = {
          // 4000 now means ONLY "Project Inactive" — the owner turned the stream off.
          // (Session teardowns moved to 4009 so the two are no longer conflated.)
          4000: 'This stream has been turned off by the project owner.',
          4001: 'No streaming server is available right now. Please try again in a few minutes.',
          4002: 'The application could not be found on the streaming server. Please contact the project owner.',
          4003: 'The application reported an error and could not continue. Refresh to try again.',
          4004: 'The session ended after reaching its maximum allowed runtime.',
          // 4005 and 4401: on the dedicated edition (EXPLICIT_CLOSE) these never get here — 4005 is a give-up and
          // 4401 a refused admission, each with its own card below; the SaaS keeps these lines.
          4005: 'The application took too long to start. Refresh the page to try again.',
          4006: 'The application closed unexpectedly. Refresh the page to start a new session.',
          // 4009 = the session ended / was torn down (e.g. a reconnect after the
          // session was already cleaned up). Distinct from 4000 project-off.
          4009: 'The session has ended. Refresh the page to start a new one.',
          // 4401 = signalling refused the first-message admission ticket (P-39;
          // only with SPX_TICKET_IN_URL=false — the SaaS never receives it).
          4401: 'The stream did not accept this session\'s access ticket. Reload the page to request a new session.',
          // Forced TURN (FORCE_TURN_ALL): signalling had no TURN relay to give this viewer.
          [RELAY_UNAVAILABLE_CODE]: relayUnavailableMessage(),
        };

        if (closeKind === CLOSE_KIND.REFUSED) {
          // Signalling refused admission (4401): the first-message ticket was
          // missing or not accepted. Terminal: no reconnect (isReconnectable
          // needs OTHER; the lib's own reconnect is switched off by
          // libs/streampixel/src/admission.js), no Retry button.
          disconnectMessageText = `Disconnected code:${ADMISSION_REQUIRED_CODE}. ${admissionRefusedMessage()}`;
          setDisconnectKind(CLOSE_KIND.REFUSED);
          setDisconnectDetail(undefined);
          setIsStreamConnect(false);
          setLoading(false);
          setShowUiBtn(false);
          setShowTcpFallbackToast(false);
        } else if (closeKind === CLOSE_KIND.CLOSED) {
          // The viewer asked for this (terminateSession / Close). It is not a
          // failure and must not read like one: quiet "Stream closed" card, no
          // close code, no automatic retry; "Start again" mints a fresh session.
          // A close from the page-leave path with the page still here says so
          // instead of "You closed the stream."
          disconnectMessageText = closedByViewerMessage({ leftPage: closedOnLeave });
          setDisconnectKind(CLOSE_KIND.CLOSED);
          setDisconnectDetail(undefined);
          setIsStreamConnect(false);
          setLoading(false);
          setShowUiBtn(false);
        } else if (closeKind === CLOSE_KIND.GAVE_UP) {
          // Signalling's own wait ran out (4005 "Streamer connection timeout"):
          // the streamer never connected. Same card as the player's own
          // give-up, Retry (fresh session), never an automatic one.
          disconnectMessageText = `Disconnected code:${data.code}. ${gaveUpMessage()}`;
          setDisconnectKind(CLOSE_KIND.GAVE_UP);
          setDisconnectDetail(undefined);
          setIsStreamConnect(false);
          setLoading(false);
          setShowUiBtn(false);
        } else if (streamerLost) {
          // ONE automatic fresh session per page life (utils/streamerLoss
          // shouldAutoRetry); the second loss, and every give-up, is manual.
          const autoRetry = shouldAutoRetry(closeKind, {
            alreadyAutoRetried: streamerLostAutoRetried,
            visible: document.visibilityState === 'visible',
          });
          disconnectMessageText = composeStreamerLostMessage({ autoRetryPending: autoRetry });
          streamerLostThisAttempt = true;
          setDisconnectKind(CLOSE_KIND.LOST);
          setDisconnectDetail(undefined);
          setIsStreamConnect(false);
          setLoading(false);
          setShowUiBtn(false);
          if (autoRetry) {
            streamerLostAutoRetried = true;
            clearTimeout(streamerLostRetryTimer);
            // Jittered (2–6 s): every viewer on a dead host is cut in the same
            // second and must not reach the mint and the scheduler together.
            streamerLostRetryTimer = setTimeout(() => {
              streamerLostRetryTimer = null;
              retryFreshSessionRef.current?.();
            }, streamerLostAutoRetryDelay());
          }
        } else if (afkTimedOut) {
          disconnectMessageText = 'Disconnected due to inactivity. The session closed after no input for a while — refresh to start again.';
        } else if (data.code === 1008) {
          disconnectMessageText = data.reason === 'Unauthorized'
            ? 'Disconnected code:1008. Access was refused — the stream ticket is invalid or has expired. Refresh the page to request a new session.'
            : `Disconnected code:1008. ${data.reason || 'The stream URL is missing required parameters.'}`;
        } else if (friendlyCloseMessages[data.code]) {
          disconnectMessageText = `${friendlyCloseMessages[data.code]} (code ${data.code})`;
        } else if (closeReasons.includes(data.reason)) {
          disconnectMessageText = `Disconnected code:${data.code}. ${data.reason}`;
        }
        setDisconnectMessage(disconnectMessageText);

        // What the embedder hears for one close, in order:
        //   1. {value:'disconnected'} with no code — the library's own close
        //      listener is registered in its constructor, before this one, and
        //      fires appStream.onDisconnect synchronously;
        //   2. this {value:'disconnected', disconnectCode, reason};
        //   3. for a close the viewer asked for, {value:'closed'} below.
        // Both 'disconnected' messages are the documented ones (docs:
        // iframe-integration/stream-states), unchanged, so an embedder keyed on
        // them keeps working; 'closed' is additive.
        window.parent.postMessage({
          type: "stream-state", value: "disconnected",
          disconnectCode: data.code,
          reason: afkTimedOut ? 'inactivity' : (closeKind === CLOSE_KIND.CLOSED ? 'viewer-closed' : data.reason),
        }, "*");
        // A close the viewer asked for also gets its own state, so an embedder
        // can tell "we closed it" from "it dropped" without parsing reasons.
        if (closeKind === CLOSE_KIND.CLOSED) {
          window.parent.postMessage({ type: 'stream-state', value: 'closed', disconnectCode: data.code, reason: 'viewer-closed' }, '*');
        }

        logStatsStart("Disconnected", { disconnectCode: data.code, reason: data.reason });

        }
      });
    }

    videoRef.current.append(appStream.rootElement);

    const videoElement = appStream?.stream?.videoElementParent?.querySelector("video");
    if (videoElement) {
      videoElement.muted = false;
      videoElement.focus();
      videoElement.autoplay = true;
      videoElement.tabIndex = 0;
    }

    hideLoadingSpinner();

    appStream.onDisconnect = function () {
      // Fired by the library's own transport 'close' listener, which runs
      // BEFORE the transport 'close' handler above — so this bare
      // 'disconnected' reaches the embedder first. The old transport fires it
      // too after a Retry: a previous attempt's late close must not tear down
      // the attempt that replaced it.
      if (!isCurrentAttempt()) return;
      setIsMuted(true);
      setRoomConnect(false);
      setToogleMicChat(false);
      setRoomDisconnect(true);
      setSfuChatActive(false);

      if (EXPLICIT_CLOSE) {
        teardownStream();
      } else {
        // SaaS: exactly as before (stopLogStats at the end, below).
        if (sfuRecoveryLoop) { clearInterval(sfuRecoveryLoop); sfuRecoveryLoop = null; }
        if (psNegotiationListener) {
          try { window.removeEventListener('streampixel:negotiation-failed', psNegotiationListener); } catch (_) {}
          psNegotiationListener = null;
        }
        clearTimeout(hostWaitTimer);
        hostWaitTimer = null;
      }

      message = { type: "stream-state", value: "disconnected" };

      document.querySelector('.loading-spinner')?.remove();

      window.parent.postMessage(message, "*");
      setIsStreamConnect(false);
      setShowTcpFallbackToast(false);
      setAuthMessage('Stream ended');
      if (!EXPLICIT_CLOSE) stopLogStats();
    };

    appStream.onVideoInitialized = function () {
      applyCodecQpOverride(appStream.stream, selectedCodec, primaryCodec);

      if (sfuPlayerRef.current && !psNegotiationListener) {
        const NEG_RECOVER_COOLDOWN_MS = 4000;
        psNegotiationListener = (ev) => {
          const now = Date.now();
          if (now - lastNegotiationRecoverAt < NEG_RECOVER_COOLDOWN_MS) return;
          lastNegotiationRecoverAt = now;
          const state = ev && ev.detail && ev.detail.state;
          console.warn('[ps-neg] negotiation failed (pcState=' + state + ') — re-driving signalling');
          try { setAuthMessage('Restoring stream…'); } catch (_) {}
          try { setNegotiationStatus('WebRTC renegotiating…'); } catch (_) {}
          try {
            const ws = appStream?.stream?.signallingProtocol?.ws;
            if (ws && ws.readyState === WebSocket.OPEN) ws.close();
          } catch (_) {}
          setTimeout(() => {
            try { setNegotiationStatus((cur) => (cur === 'WebRTC renegotiating…' ? '' : cur)); } catch (_) {}
          }, 5000);
        };
        window.addEventListener('streampixel:negotiation-failed', psNegotiationListener);
      }

      if (sfuPlayerRef.current && !sfuRecoveryLoop) {
        let unhealthySince = null;
        let messageShown = false;
        let lastFrameAt = Date.now();
        let lastFrameCount = 0;
        let trackedEl = null;
        const MESSAGE_MS = 1000;
        const FRAME_STALE_MS = 3500;
        const GIVE_UP_MS = 10 * 60000;

        // A stream that has not started yet is NOT a stream that has broken.
        // There is no <video> element until the first offer is answered, and
        // `healthy` requires one — so every tick before that counted as
        // unhealthy. Tick 1 set unhealthySince, tick 2 passed MESSAGE_MS, and
        // the page reloaded ~2s after connecting, every time: viewers appeared
        // in signalling as a fresh PlayerN every ~3.5s and never saw video.
        // Startup is held off until the stream has been healthy at least once;
        // after that a stall is real and the reload below is right.
        //
        // The grace window still expires, so a stream that genuinely never
        // starts recovers instead of hanging — just on a 45s cadence rather
        // than a 2s one.
        const STARTUP_GRACE_MS = 45000;
        const loopStartedAt = Date.now();
        let everHealthy = false;

        // Reloading is the recovery, but it clears every JS variable that could
        // remember it happened — which is what let the loop run unbounded.
        // sessionStorage survives the reload and is per-tab, so it is what makes
        // "stop after N attempts" possible at all. Wrapped because embedding
        // sites can block storage in a third-party frame.
        const RELOADS_KEY = 'sp:sfuRecoveryReloads';
        const MAX_RECOVERY_RELOADS = 3;
        const reloadCount = () => {
          try { return parseInt(sessionStorage.getItem(RELOADS_KEY), 10) || 0; } catch (_) { return 0; }
        };
        const noteReload = () => {
          try { sessionStorage.setItem(RELOADS_KEY, String(reloadCount() + 1)); } catch (_) {}
        };
        const clearReloads = () => {
          try { sessionStorage.removeItem(RELOADS_KEY); } catch (_) {}
        };

        const attachFrameTracker = (ve) => {
          if (!ve || ve === trackedEl) return;
          trackedEl = ve;
          lastFrameAt = Date.now();
          lastFrameCount = 0;
          if (typeof ve.requestVideoFrameCallback === 'function') {
            const onFrame = () => {
              if (trackedEl !== ve) return;
              lastFrameAt = Date.now();
              try { ve.requestVideoFrameCallback(onFrame); } catch (_) {}
            };
            try { ve.requestVideoFrameCallback(onFrame); } catch (_) {}
          }
        };

        sfuRecoveryLoop = setInterval(() => {
          if (document.hidden) { unhealthySince = null; lastFrameAt = Date.now(); return; }

          const ve = appStream.stream?.videoElementParent?.querySelector('video');
          attachFrameTracker(ve);
          const vtrack = (ve && ve.srcObject && typeof ve.srcObject.getVideoTracks === 'function')
            ? ve.srcObject.getVideoTracks()[0]
            : null;

          if (ve && ve.readyState >= 2 && ve.paused) {
            ve.play().catch(() => {});
          }

          let frameCount = null;
          try {
            if (ve && typeof ve.getVideoPlaybackQuality === 'function') {
              frameCount = ve.getVideoPlaybackQuality().totalVideoFrames;
            } else if (ve && typeof ve.webkitDecodedFrameCount === 'number') {
              frameCount = ve.webkitDecodedFrameCount;
            }
          } catch (_) {}
          if (frameCount != null && frameCount > lastFrameCount) {
            lastFrameCount = frameCount;
            lastFrameAt = Date.now();
          }
          const haveFrameSignal = frameCount != null
            || (!!ve && typeof ve.requestVideoFrameCallback === 'function');
          const framesFresh = haveFrameSignal
            ? (Date.now() - lastFrameAt < FRAME_STALE_MS)
            : (!!vtrack && vtrack.muted === false);
          const trackDead = !vtrack || vtrack.readyState === 'ended';
          const healthy = !!ve && ve.readyState >= 2 && !ve.paused && !trackDead && framesFresh;

          if (healthy) {
            unhealthySince = null;
            // Video is flowing, so whatever prompted an earlier reload is over.
            // Clearing here (rather than on connect) is deliberate: a reload
            // that reconnects but still shows nothing must keep its count.
            if (!everHealthy) { everHealthy = true; clearReloads(); }
            if (messageShown) {
              messageShown = false;
              setReconnect(false);
              setLoading(false);
              setShowUiBtn(true);
              setAuthMessage('');
              window.parent.postMessage({ type: 'stream-state', value: 'loadingComplete' }, '*');
            }
            return;
          }

          // Still starting up: no video element yet, or one that has not decoded
          // its first frame. Not a fault, and reloading here only restarts the
          // same wait.
          if (!everHealthy && Date.now() - loopStartedAt < STARTUP_GRACE_MS) {
            unhealthySince = null;
            return;
          }

          if (unhealthySince === null) { unhealthySince = Date.now(); return; }
          const unhealthyFor = Date.now() - unhealthySince;

          if (unhealthyFor >= GIVE_UP_MS) {
            console.warn('[sfuRecovery] host did not return after ' + GIVE_UP_MS + 'ms — giving up');
            logDiag('sfuRecoveryGaveUp', { timeSinceStart: Date.now() - startTime });
            clearInterval(sfuRecoveryLoop);
            sfuRecoveryLoop = null;
            setLoading(false);
            setShowUiBtn(false);
            setSfuChatActive(false);
            setDisconnectMessage("Host hasn't returned — tap to reconnect");
            setIsStreamConnect(false);
            window.parent.postMessage({ type: 'stream-state', value: 'disconnected' }, '*');
            return;
          }

          if (!messageShown && unhealthyFor >= MESSAGE_MS) {
            messageShown = true;

            // Reloading has not helped MAX_RECOVERY_RELOADS times running, so
            // it is not going to. Hand the viewer a control instead of putting
            // the tab into another reload it cannot interrupt.
            if (reloadCount() >= MAX_RECOVERY_RELOADS) {
              console.warn('[sfuRecovery] still no video after ' + reloadCount() + ' reloads — stopping');
              logDiag('sfuRecoveryGaveUp', {
                reason: 'reload limit', reloads: reloadCount(),
                timeSinceStart: Date.now() - startTime,
              });
              clearReloads();
              clearInterval(sfuRecoveryLoop);
              sfuRecoveryLoop = null;
              setLoading(false);
              setShowUiBtn(false);
              setSfuChatActive(false);
              setDisconnectMessage("Stream didn't come back — tap to reconnect");
              setIsStreamConnect(false);
              window.parent.postMessage({ type: 'stream-state', value: 'disconnected' }, '*');
              return;
            }

            setAuthMessage('Host disconnected — reconnecting…');
            setReconnect(true);
            setLoading(true);
            setShowUiBtn(false);
            // Both of these must happen BEFORE the reload — it tears down the
            // page, so anything after it never runs. The embedding site was
            // never getting this state change.
            window.parent.postMessage({ type: 'stream-state', value: 'reconnecting' }, '*');
            noteReload();
            window.location.reload(true);
          }
        }, 1000);
      }

      setIsMuted(true);
      setRoomConnect(false);
      setToogleMicChat(false);


      const xrBtn = document.getElementById('xrBtn');
      if (xrBtn) {
        xrBtn.style.display = "none";
      }
      setLogoStyles(logoOffsets(branding.logoPosition));

      const statsBtn = document.getElementById('statsBtn');
      setReconnect(false);
      reconnectWindowStart = null;
      reconnecting = false;

      if (statsBtn) {
        statsBtn.style.display = 'none';
        statsBtn.disabled = true;
      }
      const panel = document.getElementById('stats-panel');
      if (panel) panel.style.display = 'none';

      const endTime = Date.now();
      timeTaken = (endTime - startTime) / 1000;

      message = { type: "stream-state", value: "loadingComplete" };
      setLoading(false);
      window.parent.postMessage(message, "*");

      if (resolutionMode === "Fixed Resolution Mode" && videoElement) {
        videoElement.style.objectFit = 'contain';
      }

      if (resolutionMode === "Crop on Resize Mode") {
        const vpele = document.getElementById("streamingVideo");
        if (vpele) vpele.removeAttribute('style');
      }

      const liveWsController = appStream.stream.signallingProtocol;

      // Dedicated edition: one handler for the stream that is up, removed by
      // teardownStream — added here on every video start, it used to pile up one
      // per reconnect and per in-place retry, each holding a dead player. SaaS:
      // as before.
      const onOffline = () => {
        if (liveWsController.isConnected()) {
          if (liveWsController.ws?.readyState === WebSocket.OPEN) liveWsController.ws.close();
          liveWsController.disconnect();
        }
      };
      if (EXPLICIT_CLOSE) {
        if (offlineListener) window.removeEventListener('offline', offlineListener);
        offlineListener = onOffline;
      }
      window.addEventListener('offline', onOffline);

      liveWsController.transport.on('close', () => {
        if (!sfuPlayerRef.current) setRoomDisconnect(true);
      });

      setShowUiBtn(true);
      if (sfuPlayerRef.current) setSfuChatActive(true);

      document.querySelector('.loading-spinner')?.remove();

      setTimeout(() => {
        handleResolution(`${resx}x${resy}f`);
        hideLoadingSpinner();

        if (startChatIf !== "Dynamic" && isChatEnabled(project)) {
          setShowChatUI(true);
          setRoomConnect(true);
          setRoomDisconnect(false);
        }

        setIsHovering(project.hoverMouse);

        // Once per session (launchParamsSent), to this session's Unreal app.
        // {} is truthy, so a bare `if (launchParams)` sent an empty descriptor
        // to Unreal on every session with no query params — a guaranteed
        // "no event type field" error in any strict UE app.
        if (EXPLICIT_CLOSE) {
          if (!launchParamsSent && isCurrentAttempt() && Object.keys(launchParams).length > 0) {
            handleSendCommand(launchParams);
          }
          launchParamsSent = true;
        } else {
          // SaaS: once per page, as before.
          if (ueMessage && Object.keys(ueMessage).length > 0) {
            handleSendCommand(ueMessage);
          }
          ueMessage = null;
        }
      }, 700);

      // Microphone / webcam AFTER the stream is up, never before. Their media
      // lines were negotiated sendrecv in _onWebRtcSdpOffer, so replaceTrack()
      // lights them up with no renegotiation. Denied or ignored prompt = stream
      // unaffected, capture simply off. Mic constraints match Epic's library.
      // Do NOT call pixelStreaming.unmuteMicrophone(true) / unmuteCamera(true)
      // here: with the flag off each forces a full reconnect. The diag row
      // carries the negotiated direction — 'recvonly' there means UE's offer
      // did not accept that media back and the track is attached but unsent.
      const attachCapture = async (kind, constraints, event) => {
        const pc = appStream.stream._webRtcController?.peerConnectionController?.peerConnection;
        const transceiver = pc?.getTransceivers().find((t) => t.receiver?.track?.kind === kind);
        if (!transceiver) {
          logDiag(event, { ok: false, reason: `no-${kind}-transceiver` });
          return;
        }
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          const track = stream.getTracks().find((t) => t.kind === kind);
          if (!track || pc.connectionState === 'closed') { stream.getTracks().forEach((t) => t.stop()); return; }
          await transceiver.sender.replaceTrack(track);
          logDiag(event, { ok: true, direction: transceiver.currentDirection || transceiver.direction });
        } catch (err) {
          console.error(`${kind} capture blocked or failed:`, err);
          logDiag(event, { ok: false, reason: err?.name || String(err) });
        }
      };
      if (project.showMic) {
        attachCapture('audio', {
          video: false,
          audio: { autoGainControl: false, channelCount: 1, echoCancellation: false, latency: 0,
                   noiseSuppression: false, sampleRate: 48000, sampleSize: 16, volume: 1.0 },
        }, 'micAttach');
      } else if (project.voiceChat) {
        // Pre-warm the permission for the LiveKit room (unchanged behaviour).
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then((stream) => stream.getTracks().forEach((t) => t.stop()))
          .catch((err) => console.error('Microphone access blocked or failed:', err));
      }
      if (project.showCamera) {
        attachCapture('video', { video: true, audio: false }, 'camAttach');
      }
      // The stats reporter used to start here; it now starts in
      // _onWebRtcConnected (see startLogStats) so unplayed streams still report.
    };
  };

  function toggleMouseControl() {
    setIsHovering(prev => {
      const next = !prev;
      PixelStreamingUiApp?.stream._webRtcController.config
        .setFlagEnabled(Flags.HoveringMouseMode, next);
      return next;
    });
  }

  // Join (or re-join) the chat room from a comms message. Single source of
  // truth for both the UE data-channel and parent-iframe paths.
  // - Cancels any in-flight disconnect teardown so it can't clobber this join.
  // - Guards on roomConnectRef (live value) to avoid a double-connect against
  //   the session-begin auto-connect.
  const connectChatRoom = (value) => {
    if (!value || !isChatEnabled(project) || roomConnectRef.current) return;
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    roomName = `${projectId}#${value.roomId}`;
    userName = value.name;
    avatar = value.pfpUrl;
    setShowChatUI(true);
    setRoomConnect(true);
    setRoomDisconnect(false);
  };

  // Leave the chat room. The actual LiveKit leave fires immediately via the
  // roomDisconnect effect; the 3s timer just defers hiding the UI / clearing
  // roomConnect for a graceful teardown. Re-entrancy-safe: replaces any prior
  // pending timer, and a connectChatRoom() cancels it.
  const disconnectChatRoom = () => {
    if (!roomConnectRef.current) return;
    setRoomDisconnect(true);
    if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
    disconnectTimerRef.current = setTimeout(() => {
      setShowChatUI(false);
      setRoomConnect(false);
      disconnectTimerRef.current = null;
    }, 3000);
  };

  const handleResponseApp = (response) => {
    try {
      const newRes = JSON.parse(response);

      if (newRes.message?.type === 'comms') {
        if (newRes.message.value === "disconnect") {
          disconnectChatRoom();
        } else {
          connectChatRoom(newRes.message.value);
        }
      }

      if (newRes.message?.type === 'heartbeat') {
        document.getElementById("afkOverlayInner")?.click();
      }

      if (newRes.message === 'requestScreenshot') handleScreenshot();

      if (newRes.message?.type === 'togglehoveringmouse') {
        PixelStreamingUiApp?.stream._webRtcController.config
          .setFlagEnabled(Flags.HoveringMouseMode, newRes.message.value);
      }
    } catch (_) {
      if (response.includes("requestScreenshot")) handleScreenshot();

      if (response.includes('https')) {
        try {
          const url = new URL(response.trim());
          if (url.protocol === 'https:') {
            window.open(url.href, '_blank', 'noopener,noreferrer');
          }
        } catch (_) { /* not a valid URL — ignore */ }
      }
    }

    window.parent.postMessage(response, '*');
  };

  useEffect(() => {
    // Origin of the page that embeds this player (null when we're top-level, or
    // when the browser won't expose the ancestor). Captured once per mount.
    const embedderOrigin = (() => {
      try {
        const raw = getParentFrameUrl();
        return raw ? new URL(raw).origin : null;
      } catch (_) {
        return null;
      }
    })();

    const handleMessage = (event) => {
      // When the player is top-level, window.parent === window, so our own
      // postMessage calls self-deliver here — and pass the source check
      // below, since the window IS its own parent — then get forwarded back
      // to Unreal: an infinite reflection loop. With no embedder there is
      // nothing to bridge, so ignore all window messages.
      if (window.parent === window) return;

      // The player is embeddable on any customer site, so there is no fixed
      // origin to allowlist. Instead we honor control messages only from the
      // DIRECT embedding window, and — when the browser exposes it — only from
      // that window's origin. A hostile second frame, popup, or unrelated tab
      // (which is how the postMessage-injection attack works) is ignored.
      if (event.source !== window.parent) return;
      if (embedderOrigin && event.origin !== embedderOrigin) return;
      if (event.data == null) return;

      const { message } = event.data;

      // passwordSubmit is deliberately NOT accepted over postMessage: honoring it
      // would let an embedding page script password guesses against the stream.
      // The password is entered in the player's own UI (handlePasswordSubmit),
      // and the ticket endpoint is rate-limited server-side.

      // Dedicated edition: handled with or WITHOUT a player — between a Retry /
      // "Start again" / the auto-retry and the next player there is none, and a
      // Close there must still stop the start and tell the embedder
      // (handleTerminateSession). SaaS: only with a player, below, as before.
      if (EXPLICIT_CLOSE && message === "terminateSession") handleTerminateSession();

      if (PixelStreamingUiApp) {
        if (message === "heartbeat") document.getElementById("afkOverlayInner")?.click();

        if (message === "requestScreenshot") handleScreenshot();

        if (message?.type === "setResolution") {
          if (resolutionSetting.includes(message.value)) {
            const match = message.value.match(/\((\d+x\d+)\)/);
            if (match) {
              handleResMax(match[1], message.value);
              activeResolution = message.value;
            }
          }
        }

        if (!EXPLICIT_CLOSE && message === "terminateSession") handleTerminateSession();

        // Both elements — see setStreamAudioMuted for why the <audio>
        // element alone is not enough in SFU sessions.
        if (message === "muteAudio") {
          setStreamAudioMuted(true);
          setIsMuted(true);
        }
        if (message === "unMuteAudio") {
          setStreamAudioMuted(false);
          setIsMuted(false);
        }

        // Only genuine custom payloads are forwarded to Unreal. Control
        // messages are consumed by the player itself (above, or comms /
        // togglehoveringmouse below) and stream-state is the player's own
        // outbound vocabulary — forwarding any of them hands a strict UE app
        // JSON with no event type ("no event type field" errors, one per
        // embedder heartbeat). passwordSubmit must never reach UE at all.
        const isControlMessage =
          ["heartbeat", "requestScreenshot", "terminateSession",
            "muteAudio", "unMuteAudio", "passwordSubmit"].includes(message) ||
          ["setResolution", "comms", "togglehoveringmouse", "stream-state"]
            .includes(message?.type);

        if (!isControlMessage) handleSendCommand(event.data);
      }

      if (message?.type === "comms") {
        if (message.value === "disconnect") {
          disconnectChatRoom();
        } else {
          connectChatRoom(message.value);
        }
      }

      if (message?.type === "togglehoveringmouse") {
        setIsHovering(message.value);
        PixelStreamingUiApp?.stream._webRtcController.config
          .setFlagEnabled(Flags.HoveringMouseMode, message.value);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [startChatIf, projectId, roomConnect, showChatUI, isHovering, fetchDone]);

  // Clear any pending chat-disconnect teardown timer (and a pending streamer-loss
  // auto-retry) on unmount.
  useEffect(() => () => {
    if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
    clearTimeout(streamerLostRetryTimer);
    streamerLostRetryTimer = null;
    clearTimeout(streamerWaitTimer);
    streamerWaitTimer = null;
    streamerClassifierTimers.forEach((t) => clearTimeout(t));
    streamerClassifierTimers = [];
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }, []);

  const handleTerminateSession = () => {
    if (!EXPLICIT_CLOSE) {
      // SaaS: exactly as before — the socket closes as an ordinary close (signalling holds the seat as for a drop).
      PixelStreamingApp.disconnect();
      PixelStreamingUiApp?.stream.disconnect();
      return;
    }
    // Remembered BEFORE the socket closes: the close event is classified as
    // 'closed' (the viewer's own doing) even if the server echoes no reason.
    viewerClosed = true;
    closedOnLeave = false;
    // A Close while the one automatic retry is pending cancels it: otherwise
    // the timer fired 2–6 s later, reset viewerClosed and minted a session the
    // viewer had just asked to close. Same for a reconnect waiting out its 5 s.
    const autoRetryPending = streamerLostRetryTimer !== null;
    clearTimeout(streamerLostRetryTimer);
    streamerLostRetryTimer = null;
    const reconnectPending = reconnectTimer !== null;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
    if (reconnectPending) reconnecting = false;
    // Will a close event follow? Only if a signalling socket is open (or
    // opening). On a disconnect card, during a reconnect's wait, or while a
    // fresh session is being started (no player yet: ticket mint, connect
    // delay) the socket is gone or not there yet, so the close handler never
    // runs and the player answers itself (utils/streamerLoss terminateAction).
    const ws = PixelStreamingUiApp?.stream?.signallingProtocol?.transport?.webSocket;
    const socketWillClose = !!ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING);
    const cardUp = !isStreamConnectRef.current;
    const startPending = !PixelStreamingUiApp && !cardUp && !gateUpRef.current;
    const action = terminateAction({ socketWillClose, autoRetryPending, reconnectPending, startPending, cardUp });
    endSessionExplicitly();
    // Both optional: between a fresh-session start and the next player there
    // is no application to close.
    PixelStreamingApp?.disconnect();
    PixelStreamingUiApp?.stream?.disconnect();
    if (action === 'closed-card') {
      // A start on its way must not build a player behind the card.
      if (startPending) closeRequested = true;
      showClosedByViewer();
    } else if (action === 'ack') {
      // The session was already over and the card says why: keep it, and
      // tell the embedder its Close is done.
      window.parent.postMessage({ type: 'stream-state', value: 'closed', reason: 'viewer-closed' }, '*');
    }
  };

  // Leaving the page is a deliberate exit too: close the socket with 1000
  // "viewer-closed" and fire the beacon. pagehide ONLY: it fires on every real
  // unload — tab close, navigation, reload, the iframe being removed, desktop
  // and mobile alike — and on entering the bfcache. beforeunload is NOT a
  // leave: in an iframe it also fires when the embedding page starts a
  // navigation that never happens (a download link, a cancelled navigation,
  // "Stay" on the embedder's own leave prompt), and listening to it ended live
  // sessions the viewer was still watching. The page may still come back
  // (bfcache restore): the echo then draws the closed card, and closedOnLeave
  // makes it say the page was left rather than that the viewer pressed Close.
  useEffect(() => {
    if (!EXPLICIT_CLOSE) return undefined;   // SaaS: leaving the page is an ordinary socket close, as before
    const onLeave = () => {
      if (activeSession) { viewerClosed = true; closedOnLeave = true; }
      endSessionExplicitly();
    };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  }, []);

  // The on-screen keyboard box (showEditTextModal) lives on document.body,
  // outside the stream root, and sends to the controller that opened it. Any
  // card means that controller is gone (a fresh session builds a new one), so
  // the box goes with it instead of floating over the card and the next
  // stream, dropping whatever the viewer types.
  useEffect(() => {
    if (!EXPLICIT_CLOSE || isStreamConnect) return;
    try { editTextModalRef.current?.rootElement?.remove(); } catch (_) { /* already gone */ }
    editTextModalRef.current = null;
  }, [isStreamConnect]);

  const handleSendCommand = (descriptor) => {
    PixelStreamingUiApp?.stream.emitUIInteraction(descriptor);
  };

  const handlePasswordSubmit = async (e, pwd, { silent = false } = {}) => {
    e?.preventDefault();
    const password = pwd || enteredPassword;
    if (!password) return;
    setPasswordError('');
    lastTicketError = null;

    const success = await fetchData({password:password});

    // A Retry / "Start again" whose mint failed for a reason a later press can
    // fix: fetchData already put the card back with its button. The password
    // was not rejected — keep it, and do not show the gate.
    if (!success && lastTicketError?.onCard) return;
    // Anything else that fails ends on the gate: a gate interaction from here
    // on, not a Retry (see fetchData's catch).
    if (!success) retryingAfterLoss = false;

    if (success) {
      rememberPassword(projectId, password);
      setPasswordValid(true);
      setLoading(true);
      setShowEnterPassword(false);
      message = { type: "stream-state", value: "hidePassword" };
      window.parent.postMessage(message, "*");
    } else {
      forgetPassword(projectId);
      if (silent) {
        // A remembered password that no longer works (the owner changed it): just ask, no "incorrect" banner
        // for a password the viewer did not type this time.
        setPasswordValid(false);
        setShowEnterPassword(true);
        setLoading(false);
        hideLoadingSpinner();
        return;
      }
      // The gate fails for more than one reason and they need different advice.
      // 429 is the brute-force lockout (10 failures per project+IP in 15 min):
      // telling that viewer "incorrect password" is wrong and makes them retry
      // a password that may well be right.
      const status = lastTicketError?.status;
      if (status === 429) {
        const secs = lastTicketError.retryAfter;
        // Only the hard cap disables the field. Below it the server still checks
        // the password, so someone who knows it must be able to type it — a
        // public IP is shared, and the failures may not even be theirs.
        if (lastTicketError.hard) {
          setLockedUntil(secs > 0 ? Date.now() + secs * 1000 : 0);
          setPasswordError(
            secs > 0
              ? `Too many attempts from this network. Try again in ${formatWait(secs)}.`
              : 'Too many attempts from this network. Please try again later.'
          );
        } else {
          setPasswordError(
            lastTicketError.message || 'Too many incorrect attempts. Check the password and try again.'
          );
        }
      } else if (status === 0) {
        setPasswordError('Could not reach the server. Check your connection and try again.');
      } else if (status && status >= 500) {
        setPasswordError('Something went wrong on our side. Please try again in a moment.');
      } else {
        setPasswordError('Incorrect password. Please try again.');
      }
      setPasswordValid(false);
      setShowEnterPassword(true);
      setLoading(false);
      hideLoadingSpinner();
    }
  };

  // Mute/unmute must touch BOTH elements. Epic's StreamController puts the
  // audio track in its separate <audio> element only when audio and video
  // arrive under different stream ids, which is what a UE streamer sends.
  // The SFU offers both tracks under ONE stream id (mediasoup-sdp-bridge
  // createOffer uses a single sendMsid), so in meetings the audio track
  // stays inside the <video> element, which StartVideoMuted keeps muted, and
  // the <audio> element has no source at all. Toggling only the <audio>
  // element — what this did — flipped an empty element while the sound
  // stayed locked in the muted video: "no audio even after unmute" on every
  // meeting session, while direct sessions worked (2026-09-09).
  // Unmuting the <video> element in direct sessions is harmless: it carries
  // no audio track there.
  function setStreamAudioMuted(muted) {
    if (!PixelStreamingUiApp) return;
    const audioElement = PixelStreamingUiApp.stream._webRtcController?.streamController?.audioElement;
    const videoPlayer = PixelStreamingUiApp.stream.videoElementParent?.querySelector("video");
    if (audioElement) {
      audioElement.muted = muted;
      if (muted) {
        audioElement.pause();
      } else if (audioElement.srcObject) {
        audioElement.play().catch(() => {});
      }
    }
    if (videoPlayer) {
      videoPlayer.muted = muted;
      if (!muted && videoPlayer.paused) {
        videoPlayer.play().catch(() => {});
      }
    }
  }

  const toggleSound = () => {
    const videoPlayer = PixelStreamingUiApp && PixelStreamingUiApp.stream.videoElementParent.querySelector("video");
    if (PixelStreamingUiApp) {
        setStreamAudioMuted(!isMuted);
        setIsMuted(!isMuted);
        if (videoPlayer) {
          videoPlayer.focus();
        }
     }
  };

  const toggleFullscreen = () => {
    const doc = document.documentElement;
    const videoPlayer = PixelStreamingUiApp?.stream.videoElementParent.querySelector("video");
    // Feature-detect element fullscreen instead of sniffing the device: iPads support it (and
    // identify as a Mac by default); only iPhone lacks it and falls back to native video fullscreen.
    const canElementFullscreen = Boolean(doc.requestFullscreen || doc.webkitRequestFullscreen || doc.mozRequestFullScreen || doc.msRequestFullscreen);
    const inFullscreen = Boolean(document.fullscreenElement || document.webkitFullscreenElement);

    if (!inFullscreen && canElementFullscreen) {
      (doc.requestFullscreen?.() ||
        doc.mozRequestFullScreen?.() ||
        doc.webkitRequestFullscreen?.() ||
        doc.msRequestFullscreen?.());
      setIsFullscreen(true);
      videoPlayer?.focus();
    } else if (!canElementFullscreen) {
      if (videoPlayer?.webkitEnterFullscreen) {
        videoPlayer.webkitEnterFullscreen();
        setIsFullscreen(true);
      } else {
        console.warn("Fullscreen not supported on this iOS device.");
      }
    } else {
      (document.exitFullscreen?.() ||
        document.mozCancelFullScreen?.() ||
        document.webkitExitFullscreen?.() ||
        document.msExitFullscreen?.());
      setIsFullscreen(false);
      videoPlayer?.focus();
    }
  };

  const toggleXR = () => PixelStreamingUiApp?.stream.toggleXR();

  const toggleStats = () => {
    if (PixelStreamingUiApp) {
      setStats(getStats());
      setIsModalVisible(prev => !prev);
    }
  };

  const handleResMax = (value, resolution) => {
    const hwsplit = value.split('x');
    activeResolution = resolution;
    if (resolutionMode === "Dynamic Resolution Mode" || resolutionMode === "Crop on Resize Mode") {
      maxHeight = hwsplit[1];
      maxWidth = hwsplit[0];
      handleResize();
    } else if (PixelStreamingUiApp) {
      PixelStreamingUiApp.stream.emitConsoleCommand(`r.SetRes ${value}f`);
      document.querySelector('video')?.focus();
    }
  };

  const handleMessageNumber = (messageLen) => setNewMessage(messageLen);

  const handleRoomConnect = (value) => {
    if (value === true) setSdkConnect(true);
  };

  const handleScreenshot = () => captureScreenshot(PixelStreamingUiApp);

  return (
    <>
      {project && isChatEnabled(project)
        && (sfuPlayerRef.current ? sfuChatActive : (!loading && showUiBtn)) && (
        <VoiceChatUI
          roomConnect={roomConnect}
          roomDisconnect={roomDisconnect}
          toggleMicChat={toggleMicChat}
          onMicStateChange={setToogleMicChat}
          handleMessageNumber={handleMessageNumber}
          handleRoomConnect={handleRoomConnect}
          darkMode={darkMode}
          micStart={toggleMicChat}
          roomName={roomName}
          showChatUiMobile={showChatUiMobile}
          showChatUiDesktop={showChatUiDesktop}
          userName={userName}
          apiBase={API_URL}
          livekitUrl={LIVEKIT_URL}
          voiceToken={getVoiceToken}
          voiceChat={project.voiceChat}
          textChat={project.textChat}
          videoChat={project.videoChat ?? false}
          position={project.chatPosition}
          streamReady={!loading && showUiBtn}
          avatar={avatar}
          onCloseChatUi={closeChatUiMobile}
          onCloseChatUiDesktop={closeChatUiDesktop}
          onOpenChatUi={openChatUiMobile}
          onOpenChatUiDesktop={openChatUiDesktop}
          onTypingChange={setSomeoneTyping}
        />
      )}
      {isStreamConnect ? (
        <>
          <div className='containMain'>
            <div
              id="videoElement"
              tabIndex={0}
              ref={videoRef}
              style={loading || showEnterPassword || showSsoLogin
                ? { zIndex: -1, position: "relative" }
                : { zIndex: 10005, position: "absolute" }}
            >
                {/* The live view's copy. LoadingOverlay and DisconnectScreen
                    each draw their own, because each is a full-screen surface
                    that has to stand alone — DisconnectScreen replaces this tree
                    entirely, which is how a single shared copy went missing on
                    the terminal-disconnect path. While the overlay is up its
                    opaque backdrop covers this one, so only ever one is seen. */}
                <BrandLogo branding={branding} />

              {!showUiBtn && !(sfuPlayerRef.current && sfuChatActive) && (
                <LoadingOverlay
                  branding={branding}
                  loading={loading}
                  authMessage={authMessage}
                  showEnterPassword={showEnterPassword}
                  enteredPassword={enteredPassword}
                  setEnteredPassword={setEnteredPassword}
                  handlePasswordSubmit={handlePasswordSubmit}
                  passwordError={passwordError}
                  showSsoLogin={showSsoLogin}
                  startSsoLogin={startSsoLogin}
                  status={status}
                  lockedOut={lockedUntil > 0}
                  ssoError={ssoError}
                  unavailable={unavailable}
                />
              )}

              <>
                {status === "active" ? (
                  <>
                    <TcpFallbackToast
                      show={showTcpFallbackToast}
                      onClose={() => setShowTcpFallbackToast(false)}
                    />
                    {(sfuPlayerRef.current ? sfuChatActive : showUiBtn) && (
                      <>
                        <ControlPanel
                          project={project}
                          showChatUiMobile={showChatUiMobile}
                          showChatUI={showChatUI}
                          roomDisconnect={roomDisconnect}
                          isMobile={isMobile}
                          showChatUiDesktop={showChatUiDesktop}
                          someoneTyping={someoneTyping}
                          newMessage={newMessage}
                          sdkConnect={sdkConnect}
                          toggleMicChat={toggleMicChat}
                          handleMicChat={handleMicChat}
                          isMuted={isMuted}
                          toggleSound={toggleSound}
                          isFullscreen={isFullscreen}
                          toggleFullscreen={toggleFullscreen}
                          activeResolution={activeResolution}
                          PixelStreamingUiApp={PixelStreamingUiApp}
                          resolutionSetting={resolutionSetting}
                          handleResMax={handleResMax}
                          toggleStats={toggleStats}
                          avgQp={avgQp}
                          primaryCodec={primaryCodec}
                          xrSupport={xrSupport}
                          toggleXR={toggleXR}
                          openChatUiMobile={openChatUiMobile}
                          openChatUiDesktop={openChatUiDesktop}
                        />
                      </>
                    )}
                  </>
                ) : null /* An inactive project is drawn by LoadingOverlay's
                  "unavailable" card. The plain-text "Inactive Project" that used
                  to live here sat UNDERNEATH that overlay's opaque backdrop, so
                  it was never actually visible — and it was a second,
                  differently-styled answer to a question the card now answers. */}
              </>

              {!fetchDone && <LoadingUI message={authMessage} />}

              {isModalVisible && (
                <StatsModal
                  stats={stats}
                  onClose={() => setIsModalVisible(false)}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <DisconnectScreen
          disconnectMessage={disconnectMessage}
          branding={branding}
          kind={disconnectKind || undefined}
          detail={disconnectDetail}
          onRetry={disconnectKind ? () => retryFreshSessionRef.current?.() : undefined}
        />
      )}
    </>
  );
};

export default App;
